/* eslint-disable @typescript-eslint/no-explicit-any */
import crypto from "crypto";
import { gunzipSync, strFromU8 } from "fflate";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from "google-libphonenumber";
import _, { assign, chain, cloneDeep, compact, countBy, extend, filter, find, findIndex, findKey, first, flatMap, forEach, fromPairs, get, identity, includes, isEmpty, isEqual, isNil, isNumber, isObject, isPlainObject, isString, join, keys, map, maxBy, merge, min, orderBy, pickBy, random, reduce, some, sortBy, sumBy, times, toPairs, transform, uniqueId, values } from "lodash";
import { Moment, default as moment, default as momentTz, unitOfTime } from "moment-timezone";
import { v4 as uuidv4 } from "uuid";
import _statuses from "./files/status.json";
import { ButlerServiceGroup, Meals, ProductCategoryId, SYDNEY_TZ, TreeData, WidgetRouteOptions } from "./types";
import { Assignment, Booking } from "./types/Booking";
import { Area, AreaGroup, Areas, Box, Boxes, Level, Placements, Section, Table } from "./types/Box";
import { AreaFlips, FloorPlanAvailability } from "./types/FloorPlan";
import { FunctionBooking } from "./types/FunctionBooking";
import { Guest } from "./types/Guest";
import { Label, Labels } from "./types/Labels";
import { Maestro, MaestroDate } from "./types/Maestro";
import { BatchSetting, Course, CourseGroup, GroupHeading, GroupHeadings, Menu, MenuItem, MenuPayments, Packages, PaymentRequirementTypes, PriceReleaseSchedule } from "./types/Menu";
import { Bucket, Buckets, OrderItem, OrderItemStatus } from "./types/Order";
import { AvailableTabPayment, PaymentTab } from "./types/PaymentTab";
import { PaymentPriceAdjustmentPayload } from "./types/Payments/PaymentPriceAdjustmentPayload";
import { Bills, LineItem, PaymentTypeId, Transaction, TransactionSurcharge } from "./types/Payments/transaction";
import { ParsedPhone } from "./types/Phone";
import { Restaurant, Restaurants } from "./types/Restaurant";
import { AllocationRule, AllocationRules } from "./types/Rules";
import { Intervals, OpeningTimes, Seating, Seatings } from "./types/Seating";
import { BankTransferFee, EftposPayments, OnlinePayments } from "./types/SubscriptionFees";
import { Customer, Customers } from "./types/customer";
import { Product, ProductGroupPriceAdjustmentType, ProductSize, Products } from "./types/product";
import { MoneyUtils } from "./utils/MoneyUtils";

export const DATE_PATTERN_YYYYMMDD = /^\d{4}-\d{2}-\d{2}$/;

export function getLineItemTotalBeforePaymentPriceAdjustment(lineItem: LineItem) {
  let total = lineItem.total;
  forEach(lineItem.paymentPriceAdjustments, (ppa) => {
    const adjustmentAmount = ppa.accountGroupAdjustmentType === ProductGroupPriceAdjustmentType.Increase ? -Number(ppa.amount) : Number(ppa.amount);
    total += adjustmentAmount;
  });
  return total;
}

export function anyIntersection(obj1: { [k: string]: any }, obj2: { [k: string]: any }): boolean {
  // Early exit for null/undefined
  if (!obj1 || !obj2) return false;

  // Use Set for O(1) lookup
  const keys = Object.keys(obj1);
  const set2 = new Set(Object.keys(obj2));

  // Find first intersection using Set.has() which is O(1)
  return keys.some((key) => set2.has(key));
}

export function hasArrayIntersection(arr1: any[], arr2: any[]): boolean {
  const set1 = new Set(arr1);
  for (const value of arr2) {
    if (set1.has(value)) {
      return true;
    }
  }
  return false;
}

export function getTicks(date: Moment) {
  if (date && date?.hours && date?.minutes) {
    return 1000 * (3600 * date.hours() + 60 * date.minutes());
  }
  return null;
}

export function getTicksAdjusted(date: Moment) {
  if (date && date?.hours && date?.minutes) {
    const ticks = 1000 * (3600 * date.hours() + 60 * date.minutes());
    if (ticks < 5 * 60 * 60 * 1000) {
      //less than 5am
      return ticks + 24 * 60 * 60 * 1000; //add 1 day
    }
    return ticks;
  }
  return null;
}

export function isTickInBetween(tick1: number, tick2: number, givenTick: number) {
  const tick1Moment = moment.utc(tick1);
  const tick2Moment = moment.utc(tick2);
  const givenTickMoment = moment.utc(givenTick);
  if (tick2 < tick1) {
    tick2Moment.add(1, "day");
  }
  return givenTickMoment.isBetween(tick1Moment, tick2Moment, undefined, "[)");
}

export function getEnumName<T>(enumType: T, value: number): keyof T | undefined {
  for (const key in enumType) {
    if (enumType[key as string] === value) {
      return key as keyof T;
    }
  }
  return undefined; // If no match is found
}

/**
 * Generates formatted time range
 * @param { number } timeStart
 * @param { number } timeEnd
 * @returns { string }
 */
export const getFormattedTimeRange = (timeStart: number, timeEnd: number): string => {
  let formatted = "";
  formatted += moment(timeStart).utc().format("hh:mmA");
  formatted += timeEnd > timeStart ? ` - ${moment(timeEnd).utc().format("hh:mmA")}` : "";
  return formatted;
};

export function getEndTime(booking: Booking | FunctionBooking) {
  if (!booking) return 0;
  const _date2 = Number(booking.intervalId);
  return _date2 + booking.alg.duration * 60 * 1000;
}

export function getThresholdLeftRight(box: Table | Section, rule: AllocationRule, threshold, override = false, staticOnly = false) {
  if (staticOnly) return [false, false];
  const thresholdReached = rule?.physicalMaxThreshold && (threshold >= rule?.physicalMaxThresholdValue || override);
  const thresholdReactedTop = thresholdReached;
  const thresholdReactedBottom = thresholdReached;
  let thresholdReachedLeft = thresholdReached;
  let thresholdReachedRight = thresholdReached;
  if (box.thresholdOverrideLeft !== undefined && !override) thresholdReachedLeft = threshold >= Number(box.thresholdOverrideLeft);
  if (box.thresholdOverrideRight !== undefined && !override) thresholdReachedRight = threshold >= Number(box.thresholdOverrideRight);
  return [thresholdReactedTop, thresholdReachedRight, thresholdReactedBottom, thresholdReachedLeft];
}

function defaultCourseDuration(booking: Booking | FunctionBooking, defaultMenus, mealId) {
  if (defaultMenus) {
    const defaultMenuId = findKey(defaultMenus, (df) => df.mealId === mealId);
    if (defaultMenuId && defaultMenus[defaultMenuId].durations) {
      const durationId = findKey(defaultMenus[defaultMenuId].durations, (d) => booking.pax >= d.minPax && Number(booking.pax) <= d.maxPax);
      if (durationId) {
        return Number(defaultMenus[defaultMenuId].durations[durationId].duration);
      }
    }
  }
  return 120;
}

export function findCategorizedProductById(butlerServiceGroups: ButlerServiceGroup[], productId) {
  for (let i = 0; i < butlerServiceGroups.length; i++) {
    const pc = butlerServiceGroups[i];
    for (let j = 0; j < pc.subcategories.length; j++) {
      if (pc.subcategories[j].butlerProducts) {
        const productIndex = findIndex(pc.subcategories[j].butlerProducts, (p) => p.productId === productId);
        if (productIndex > -1) return pc.subcategories[j].butlerProducts[productIndex];
      }
    }
  }
  return null;
}

// export function containsPrepayment(categories, butlerServiceGroups: ButlerServiceGroup[]) {
//   let prepayment = null;
//   if (categories) {
//     for (let i = 0; i < categories.length; i++) {
//       if (!categories[i]) {
//         categories[i] = { productId: "", subcategoryId: "", quantity: 0 };
//       } else {
//         const cat = categories[i];
//         const product = findCategorizedProductById(butlerServiceGroups, cat.productId);
//         if (cat && cat.productId && cat.quantity > 0 && product) {
//           if (product.butlerService) {
//             if (product.butlerService && product.butlerService[cat.productSizeId] && product.butlerService[cat.productSizeId].requiredPrepayment) {
//               prepayment = true;
//             }
//           }
//         }
//       }
//     }
//   }
//   return prepayment;
// }

export function getDuration(booking: Booking | FunctionBooking, seating: Seating, course: Course, defaultMenus, hardEndTime: number, level = 0, timeProduct = null): number {
  if (!booking) return null;
  const reset = seating && !("functionName" in booking) ? Number(seating.reset || 0) : 0;
  let end = null;
  if (booking.statusChangeData && booking.statusChangeData[_statuses.vacated.text] && booking.statusChangeData[_statuses.vacated.text] - Number(booking.intervalId) > 0) {
    return (booking.statusChangeData[_statuses.vacated.text] - Number(booking.intervalId)) / (1000 * 60);
  } else if ("functionName" in booking && booking.endTime) {
    return (booking.endTime - Number(booking.intervalId)) / (60 * 1000) + reset;
  } else if (booking.endTime) {
    return (booking.endTime - Number(booking.intervalId)) / (60 * 1000);
  } else if (seating && seating.useSeatingEndTime) {
    end = (Number(seating.close) - Number(booking.intervalId)) / (60 * 1000);
  } else if (course && course.durations) {
    for (const d in course.durations) {
      const duration = course.durations[d];
      if (Number(duration.minPax) <= Number(booking.pax) && Number(duration.maxPax) >= Number(booking.pax)) {
        switch (level) {
          case 2:
            end = Number(duration.vip) + reset;
            break;
          case 1:
            end = Number(duration.supervip) + reset;
            break;
          default:
            end = Number(duration.standard) + reset;
            break;
        }
      }
    }
  }
  if (!end) {
    end = defaultCourseDuration(booking, defaultMenus, booking.mealId) + reset;
  }
  if (timeProduct) {
    return end + (timeProduct ? Number(timeProduct.timeValue) : 0);
  }
  const endTime = Number(booking.intervalId) + end * 60 * 1000;
  if (!!hardEndTime && endTime > hardEndTime) {
    return (hardEndTime - Number(booking.intervalId)) / (60 * 1000);
  }
  return end;
}

export function makeId(length = 6) {
  let text = "";
  const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

export function generateRandomString(length) {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    result += charset.charAt(randomIndex);
  }
  return result;
}

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object, base) {
  return transform(object, (result, value, key) => {
    if (!isEqual(value, base[key])) {
      result[key] = isObject(value) && isObject(base[key]) ? difference(value, base[key]) : value;
    }
  });
}

function getIntervalIdByTicks(seating: Seating, start) {
  for (const intervalId in seating.intervals) {
    if (seating.intervals[intervalId].enabled === true && seating.close >= Number(intervalId) && Number(intervalId) > start) {
      return intervalId;
    }
  }
  return "";
}

/** @deprecated moved this logic to getCurrentMaestro and added more logic in getCurrentMaestro to get appropriate date in between 12am to 5am */
export function getMaestroNow(seatings: Seatings, date, restaurantId, zoneId): { date: string; restaurantId: string; mealId: string; closed: boolean } {
  const seatings1 = chain(seatings).values().orderBy(["open"]).value();
  const now = momentTz.tz(zoneId);
  const date1 = momentTz.tz(date, zoneId);
  const isToday = now.isSame(date1, "day");
  if (isToday === true) {
    for (let i = 0; i < seatings1.length; i++) {
      const seating = seatings1[i];
      const start = (now.hour() * 3600 + now.minute() * 60) * 1000;
      const intervalId = getIntervalIdByTicks(seating, start);
      if (intervalId) {
        return { date: date1.format("YYYYMMDD"), restaurantId, mealId: seating.mealId, closed: Number(intervalId) < seating.open };
      }
    }
  } else {
    for (let i = 0; i < seatings1.length; i++) {
      const seating = seatings1[i];
      const intervalId = getIntervalIdByTicks(seating, 0);
      if (intervalId) {
        return { date: date1.format("YYYYMMDD"), restaurantId, mealId: seating.mealId, closed: Number(intervalId) < seating.open };
      }
    }
  }
  return { date: date1.format("YYYYMMDD"), restaurantId, mealId: "", closed: true };
}

export function getBoxesFromFloorPlanAvailability(availability: FloorPlanAvailability, boxes: Boxes) {
  let floorPlanIds = [];
  if (availability.defaultFloorPlanId) floorPlanIds = [availability.defaultFloorPlanId];
  floorPlanIds = [...floorPlanIds, ...(availability.additionalFloorPlanIds || [])];
  const boxes1: Boxes = {};
  for (let i = 0; i < floorPlanIds.length; i++) {
    const floorPlanId = floorPlanIds[i];
    Object.assign(
      boxes1,
      pickBy(boxes, (b) => {
        if (b.areaVariationId === availability.areaVariations?.[floorPlanId]?.[b.areaId]?.defaultAreaVariationId) return true;
        if (findIndex(availability.areaVariations?.[floorPlanId]?.[b.areaId]?.additionalAreaVarationIds, (areaVariationId) => areaVariationId === b.areaVariationId) > -1) return true;
        return false;
      })
    );
  }
  return boxes1;
}

export function getBoxesByAreaVariationId(boxes: Box[], areas: Areas, availability: FloorPlanAvailability, areaFlips: AreaFlips, ticks): Box[];
export function getBoxesByAreaVariationId(boxes: Boxes, areas: Areas, availability: FloorPlanAvailability, areaFlips: AreaFlips, ticks): Boxes;
export function getBoxesByAreaVariationId(boxes: Boxes | Box[], areas: Areas, availability: FloorPlanAvailability, areaFlips: AreaFlips, ticks): Box[] | Boxes {
  let timeId = "";
  if (keys(areaFlips).length > 0) {
    const times = chain(areaFlips)
      .keys()
      .map((t) => t.substr(1, t.length - 1))
      .value()
      .sort()
      .reverse();
    const time = find(times, (t) => t <= ticks);
    if (!time) timeId = `s${times[times.length - 1]}`;
    else timeId = `s${time}`;
  }

  const isArray = Array.isArray(boxes);
  let boxes1: Boxes = {};
  if (isArray) {
    boxes1 = reduce(
      boxes,
      (obj, param) => {
        obj[param._key] = param;
        return obj;
      },
      {}
    );
  } else {
    boxes1 = { ...boxes };
  }

  const allBoxes: Boxes = {};
  const floorPlanId = areaFlips?.[timeId]?.floorPlanId || availability?.defaultFloorPlanId;
  for (const areaId in areas) {
    const index = findIndex(areaFlips?.[timeId]?.areaVariations, (av) => av.areaId === areaId);
    if (index > -1) {
      const av = areaFlips?.[timeId]?.areaVariations[index];
      const areaBoxes: Boxes = pickBy(boxes1, (a) => a.enabled !== false && a.areaId === areaId && a.areaVariationId === av.areaVariationId);
      Object.assign(allBoxes, areaBoxes);
    } else {
      const areaBoxes: Boxes = pickBy(boxes1, (a) => a.enabled !== false && a.areaId === areaId && a.areaVariationId === availability.areaVariations?.[floorPlanId]?.[areaId]?.defaultAreaVariationId);
      Object.assign(allBoxes, areaBoxes);
    }
  }
  return isArray ? (map(allBoxes, (value, key) => extend({}, value, { _key: key })) as Box[]) : allBoxes;
}

export function getSeatingId(seatings: Seatings, intervalId) {
  for (const seatingId in seatings) {
    const seating = seatings[seatingId];
    if (seating.intervals && seating.intervals[intervalId]) {
      return seatingId;
    }
  }
  return "";
}

export function isIgnored(label: Label, zoneId, date) {
  const day = momentTz.tz(String(date), "YYYYMMDD", zoneId).format("dddd");
  return label.range === "range" ? !!label.ignored && label.ignored[day] === true : label.discreteDates && label.discreteDates.findIndex((discrete) => discrete.ignored === true && String(discrete.date) === momentTz.tz(String(date), "YYYYMMDD", zoneId).format("YYYYMMDD")) > -1;
}

export function isClosed(label: Label, zoneId, date) {
  const m = momentTz.tz(String(date), "YYYYMMDD", zoneId).format("dddd");
  return label.range === "range" ? !!label.closed && label.closed[m] === true : label.discreteDates && label.discreteDates.findIndex((discrete) => discrete.closed === true && String(discrete.date) === String(date)) > -1;
}

export function getPastLabelId(label: Label, labelId, zoneId, date, restaurantId, mealId) {
  if (!isClosed(label, zoneId, date) && label.restaurantId === restaurantId && label.production && label.mealId === mealId) {
    return labelId;
  }
  return "";
}

export function getLabelId(labels: Labels, zoneId, date: string, restaurantId, mealId) {
  const labels1: Labels = pickBy(labels, (label) => {
    return label.enabled && !isIgnored(label, zoneId, date) && label.restaurantId === restaurantId && label.production && label.mealId === mealId && (label.range === "range" ? label.start <= momentTz.tz(date, "YYYYMMDD", zoneId).startOf("day").valueOf() && label.end >= momentTz.tz(String(date), "YYYYMMDD", zoneId).startOf("day").valueOf() : label.discreteDates?.findIndex((discrete) => String(discrete.date) === date) > -1);
  });
  const labelsSorted = chain(labels1)
    .map((label: Label, key) => ({ ...label, _key: key }))
    .sortBy((label) => label.order)
    .value() as Label[];
  const label: Label = first(labelsSorted);
  if (label) {
    return label._key;
  }
  return "";
}

export function getDateLabelId(labels: Labels, zoneId, date, restaurantId) {
  const labels1 = pickBy(labels, (label) => {
    return label.enabled && !isIgnored(label, zoneId, date) && label.restaurantId === restaurantId && label.production && (label.range === "range" ? label.start <= momentTz.tz(date, "YYYYMMDD", zoneId).startOf("day").valueOf() && label.end >= momentTz.tz(date, "YYYYMMDD", zoneId).startOf("day").valueOf() : label.discreteDates.findIndex((discrete) => Number(discrete.date) === Number(date)) > -1);
  });
  const labelsSorted = chain(labels1)
    .map((label, key) => ({ ...label, _key: key }))
    .sortBy((label) => label.order)
    .value();
  const label = first(labelsSorted);
  if (label) {
    return label._key;
  }
  return "";
}

export function getMealLabelIds(labels: Labels, zoneId: string, date: string, restaurantId) {
  const groupLabels = chain(labels)
    .pickBy((label) => label.restaurantId === restaurantId)
    .groupBy("mealId")
    .value();
  const mealLabels: Labels = {};
  for (const mealId in groupLabels) {
    const labelsFiltered: Labels = pickBy(labels, (label) => {
      return label.mealId === mealId && label.enabled && !isIgnored(label, zoneId, date) && label.restaurantId === restaurantId && label.production && (label.range === "range" ? label.start <= momentTz.tz(date, "YYYYMMDD", zoneId).startOf("day").valueOf() && label.end >= momentTz.tz(date, "YYYYMMDD", zoneId).startOf("day").valueOf() : label.discreteDates && label.discreteDates.findIndex((discrete) => String(discrete.date) === date) > -1);
    });
    const labelId = getLabelId(labelsFiltered, zoneId, date, restaurantId, mealId);
    if (labelId && !isClosed(labelsFiltered[labelId], zoneId, Number(date))) {
      mealLabels[labelId] = labelsFiltered[labelId];
    }
  }
  return mealLabels;
}

export function getAreaId(placements: Placements, areas: (Area | Level)[], rowIndex: number, colIndex: number) {
  return findKey(placements, (placement, areaId) => {
    if (findIndex(areas, (area) => area._key === areaId && area.enabled) >= 0) {
      return placement.x === colIndex && placement.y === rowIndex;
    }
    return false;
  });
}

export function getIntervalsFromSeatings(seatings: Seatings) {
  const results: Intervals = {};
  for (const seatingId in seatings) {
    const seating = seatings[seatingId];
    const intervals = pickBy(seating.intervals, (interval) => interval.enabled === true);
    Object.assign(results, intervals);
  }
  return results;
}

export function getBookingsAtOneTime(interval: number, bookings: (Booking | FunctionBooking)[]) {
  return filter(bookings, (b) => !b.alg.waitlist && Number(b.intervalId) <= interval && getEnd(b) > interval);
}

export function isBeveragePaymentRequired(booking: Booking, transactions: Transaction[], packages: Packages) {
  return isAdultBeveragePaymentRequired(booking, transactions, packages) || isChildBeveragePaymentRequired(booking, transactions, packages);
}

export function isAdultBeveragePaymentRequired(booking: Booking, transactions: Transaction[], packages: Packages) {
  if (booking.adultBeveragePackageId && packages[booking.adultBeveragePackageId]?.standardRequiresPrepayment === true) {
    const index = findIndex(transactions, (t) => findIndex(t.lineItems, (li) => li.productCategoryId === ProductCategoryId.PrepaidMenus && li.id1 === booking.adultBeveragePackageProductId && li.id2 === booking.adultBeveragePackageId) > -1);
    if (index === -1) return true;
  }
  return false;
}

export function isChildBeveragePaymentRequired(booking: Booking, transactions: Transaction[], packages: Packages) {
  if (booking.childBeveragePackageId && packages[booking.childBeveragePackageId]?.standardRequiresPrepayment === true) {
    return some(transactions, (t) => some(t.lineItems, (li) => li.productCategoryId === ProductCategoryId.PrepaidMenus && li.id1 === booking.childBeveragePackageProductId && li.id2 === booking.childBeveragePackageId));
  }
  return false;
}

export function isAdultBeveragePaid(booking: Booking, transactions: Transaction[]) {
  return some(transactions, (t) => some(t.lineItems, (li) => li.productCategoryId === ProductCategoryId.PrepaidMenus && li.id1 === booking.adultBeveragePackageProductId && li.id2 === booking.adultBeveragePackageId));
}

export function isChildBeveragePaid(booking: Booking, transactions: Transaction[]) {
  return some(transactions, (t) => some(t.lineItems, (li) => li.productCategoryId === ProductCategoryId.PrepaidMenus && li.id1 === booking.childBeveragePackageProductId && li.id2 === booking.childBeveragePackageId));
}

export function isMenuPaymentRequired(booking: Booking, transactions: Transaction[], menuPayments: MenuPayments) {
  if (!isNil(menuPayments?.[booking.courseId]?.paymentRequirementTypeId)) {
    if (menuPayments[booking.courseId].paymentRequirementTypeId === PaymentRequirementTypes.CreditCardDetails) {
      // CC details only
      return !booking.casual && !(booking.creditCardDetailsConfirmed || booking.ccOverride);
    }
    let isDisabled = false;
    if (transactions) {
      for (let i = 0; i < transactions.length; i++) {
        if (!sumBy(transactions[i].lineItems, (li) => getLineItemTotalBeforePaymentPriceAdjustment(li))) continue;
        isDisabled = findIndex(transactions[i].lineItems, (t) => t.productCategoryId === ProductCategoryId.PrepaidMenus || t.productCategoryId === ProductCategoryId.DepositMenus) > -1;
        if (isDisabled) break;
      }
    }
    const paymentType = menuPayments[booking.courseId]?.paymentRequirementTypeId;
    if (!isDisabled && paymentType) {
      return true;
    }
  }
  return false;
}

export const getNoShowFee = (numAdults: number, numChildren: number, booking: Booking, menu: Menu, products: Product[]) => {
  let adultFee = 0;
  let childFee = 0;
  if (booking.menuId && !booking?.alg?.onHold) {
    const mp = menu.menuPayments[booking.courseId];
    const pt = mp?.paymentType;
    if (pt?.enableNoShow) {
      if (pt.noShowFeeTypeId === 3) {
        // $
        adultFee = pt.adultNoShowFeeAmount * numAdults;
        childFee = pt.childNoShowFeeAmount * numChildren;
      } else if (Number(pt.noShowFeeTypeId) === 1 || Number(pt.noShowFeeTypeId) === 2) {
        // %
        let product = find(products, (p) => p.id === booking.menuOptionId);
        if (product) {
          const price = Number(product.restaurants[booking.restaurantId].price[product.fixedPriceMenuTypes.adult]);
          adultFee = (pt.adultNoShowFeeAmount / 100) * price;
          for (let j = 0; j < (booking.guests || []).length; j++) {
            const guest = booking.guests[j];
            product = find(products, (p) => p.id === guest.menuOptionId);
            if (product) {
              if (!guest.childOrDependent) {
                adultFee += (pt.adultNoShowFeeAmount / 100) * Number(product.restaurants[booking.restaurantId].price[product.fixedPriceMenuTypes.adult] as number);
              } else {
                childFee += (pt.childNoShowFeeAmount / 100) * Number(product.restaurants[booking.restaurantId].price[product.fixedPriceMenuTypes.adult] as number);
              }
            }
          }
          const func = Number(pt.roundingTypeId) === 1 ? Math.ceil : Math.floor;
          if (Number(pt.nearestTypeId) === 1) {
            // nearest $1
            adultFee = func(adultFee / 1) * 1;
            childFee = func(childFee / 1) * 1;
          } else if (Number(pt.nearestTypeId) === 2) {
            // nearest $5
            adultFee = func(adultFee / 5) * 5;
            childFee = func(childFee / 5) * 5;
          } else if (Number(pt.nearestTypeId) === 3) {
            // nearest $10
            adultFee = func(adultFee / 10) * 10;
            childFee = func(childFee / 10) * 10;
          }
        }
      }
    }
  }
  return { adultFee: Math.round(+adultFee), childFee: Math.round(+childFee) };
};

export const getCancellationFee = (numAdults: number, numChildren: number, booking: Booking, menu: Menu, products: Product[]) => {
  let adultFee = 0;
  let childFee = 0;
  if (booking.menuId && !booking?.alg?.onHold && menu) {
    const mp = menu.menuPayments?.[booking.courseId];
    const pt = mp?.paymentType;
    if (pt?.enableCancellation) {
      const m1 = moment(String(booking.date), "YYYYMMDD").add(booking.intervalId / (60 * 1000), "minutes");
      const m2 = moment(booking.cancellationDate);
      const numDays = Math.ceil(m1.diff(m2, "days", true));
      // const numAdults = Number(pax) - Number(children || 0);
      // const numChildren = Number(children || 0);
      for (let i = 0; i < pt.timeRules.length; i++) {
        const tr = pt.timeRules[i];
        if (numDays >= tr.from && numDays <= tr.to) {
          if (pt.feeTypeId === 3) {
            // $
            adultFee = tr.adultAmount * numAdults;
            childFee = tr.childAmount * numChildren;
            break;
          } else if (pt.feeTypeId === 1 || pt.feeTypeId === 2) {
            // %
            let product = find(products, (p) => p.id === booking.menuOptionId);
            if (product) {
              const price = Number(product.restaurants[booking.restaurantId].price[product.fixedPriceMenuTypes.adult]);
              adultFee = (tr.adultAmount / 100) * price;
              for (let j = 0; j < (booking.guests || []).length; j++) {
                const guest = booking.guests[j];
                product = find(products, (p) => p.id === guest.menuOptionId);
                if (product) {
                  if (!guest.childOrDependent) {
                    adultFee += (tr.adultAmount / 100) * Number(product.restaurants[booking.restaurantId].price[product.fixedPriceMenuTypes.adult] as number);
                  } else {
                    childFee += (tr.childAmount / 100) * Number(product.restaurants[booking.restaurantId].price[product.fixedPriceMenuTypes.adult] as number);
                  }
                }
              }
              const func = pt.roundingTypeId === 1 ? Math.ceil : Math.floor;
              if (pt.nearestTypeId === 1) {
                // nearest $1
                adultFee = func(adultFee / 1) * 1;
                childFee = func(childFee / 1) * 1;
              } else if (pt.nearestTypeId === 2) {
                // nearest $5
                adultFee = func(adultFee / 5) * 5;
                childFee = func(childFee / 5) * 5;
              } else if (pt.nearestTypeId === 3) {
                // nearest $10
                adultFee = func(adultFee / 10) * 10;
                childFee = func(childFee / 10) * 10;
              }
            }
          }
        }
      }
    }
  }
  return { adultFee: Math.round(+adultFee), childFee: Math.round(+childFee) };
};

export function getEnd(booking: Booking | FunctionBooking) {
  if ("functionName" in booking) {
    return Number(booking.intervalId) + Number(booking.alg?.duration || 0) * 60 * 1000 + Number(booking.resetTime || 0) * 60 * 1000;
  }
  return Number(booking.intervalId) + Number(booking.alg?.duration || 0) * 60 * 1000;
}

export function getBookingsBetweenTwoTimes(sectionId: string, boxId: string, bookingToAdd: Booking | FunctionBooking, bookings: (Booking | FunctionBooking)[]) {
  if (!bookingToAdd) return [];
  if (!bookingToAdd.alg.duration) {
    console.error(`duration is empty! ${bookingToAdd._key}`);
  }

  const bookingToAddStart = "functionName" in bookingToAdd ? Number(bookingToAdd.intervalId) - Number(bookingToAdd.setupTime || 0) * 60 * 1000 : Number(bookingToAdd.intervalId);
  const bookingToAddEnd = getEnd(bookingToAdd);

  // const bookingToAddResetTime = "functionName" in bookingToAdd ? bookingToAdd.resetTime || 0 : bookingToAdd.alg.reset || 0;

  return filter(bookings, (b) => {
    if (b._key === bookingToAdd._key) return false;
    if ("functionName" in b && !b.nonExclusive && !keys(b.alg.areaIds).length) return false;
    if ("functionName" in b && b.nonExclusive && !b.alg.tables) return false;
    if (!("functionName" in b) && !b.alg.tables) return false;

    if (sectionId) {
      const matchesSectionDirect = b.alg.sectionId === sectionId;
      const matchesSectionProximity = some(b.alg.proximity, (p) => p.sectionId === sectionId);
      if (!matchesSectionDirect && !matchesSectionProximity) return false;
    }

    if (boxId) {
      const matchesBoxDirect = b.alg.tables[boxId];
      const matchesBoxProximity = some(b.alg.proximity, (p) => p.tables?.[boxId] !== undefined);
      if (!matchesBoxDirect && !matchesBoxProximity) return false;
    }

    // const effectiveReset = bookingToAdd.intervalId < b.intervalId ? bookingToAddResetTime * 60 * 1000 : 0;

    // const start = "functionName" in b ? Number(b.intervalId) - Number(b.setupTime || 0) * 60 * 1000 + effectiveReset : Number(b.intervalId);
    let start = Number(b.intervalId) - ("functionName" in b ? Number(b.setupTime || 0) * 60 * 1000 : 0);
    const end = getEnd(b);

    let bookingToAddEffectiveStart = bookingToAddStart;

    // only modify the effective start time if both bookingToAdd and b are functions if they both to prevent doubling up on setup/reset times
    // we can artificially push the start time back which will allow setups and resets to intersect
    if ("functionName" in bookingToAdd && "functionName" in b) {
      if (bookingToAdd.intervalId > b.intervalId && !!bookingToAdd.setupTime && !!b.resetTime) {
        bookingToAddEffectiveStart += Math.min(bookingToAdd.setupTime, b.resetTime) * (60 * 1000);
      } else if (b.intervalId > bookingToAdd.intervalId && !!bookingToAdd.resetTime && !!b.setupTime) {
        start += Math.min(bookingToAdd.resetTime, b.setupTime) * (60 * 1000);
      }
    } else if ("functionName" in bookingToAdd && bookingToAdd.intervalId > b.intervalId && !!bookingToAdd.setupTime && !!b.alg.reset) {
      bookingToAddEffectiveStart += Math.min(bookingToAdd.setupTime, b.alg.reset) * (60 * 1000);
    } else if ("functionName" in b && b.intervalId && bookingToAdd.intervalId && !!b.setupTime && !!bookingToAdd.alg.reset) {
      start += Math.min(b.setupTime, bookingToAdd.alg.reset) * (60 * 1000);
    }
    //

    const overlapsTime = (start <= Number(bookingToAddEffectiveStart) && end > Number(bookingToAddEffectiveStart)) || (start < bookingToAddEnd && end >= bookingToAddEnd) || (start >= Number(bookingToAddEffectiveStart) && end <= bookingToAddEnd);

    return overlapsTime;
  });
}

/**@deprecated */
export function getPaymentsFromBooking(booking: Booking | FunctionBooking, productSizes: ProductSize[], butlerServiceGroups: ButlerServiceGroup[] = []) {
  const paymentSummary = { ...booking.paymentSummary };

  function convertFieldsToDollar(obj, fieldsToConvert) {
    function recurse(current) {
      if (_.isArray(current)) {
        _.forEach(current, recurse);
      } else if (_.isPlainObject(current)) {
        _.forOwn(current, (value, key) => {
          if (value && fieldsToConvert.includes(key) && _.isNumber(value)) {
            current[key] = MoneyUtils.centsToDollars(value);
          } else {
            recurse(value);
          }
        });
      }
    }
    recurse(obj);
    return obj;
  }
  convertFieldsToDollar(paymentSummary, ["grandTotal", "total", "price", "value", "salesTax", "totalSalesTax", "purchaseAmount", "subTotal", "serviceFeeAmount", "resbutlerFeeAmount"]);

  const preorderLineItems = [];
  const requiredLineItems = [];
  const prepaidLineItems = [];
  let totalPreorder1 = 0;
  let totalRequired1 = 0;
  let totalPrepaid1 = 0;
  let serviceFee = 0;
  const surcharges = [];
  const serviceCharges = [];
  let salesTax = 0;
  if (!("functionName" in booking) && paymentSummary) {
    if (paymentSummary.lineItems) {
      for (let i = 0; i < paymentSummary.lineItems.length; i++) {
        const { id1, name, description, price, quantity, total, lineItemTypeId, productCategoryId } = paymentSummary.lineItems[i];
        if ((paymentSummary.requiredPaymentTypeId !== 1 || !booking.ccOverride) && (productCategoryId === ProductCategoryId.DepositMenus || productCategoryId === ProductCategoryId.PrepaidMenus || productCategoryId === ProductCategoryId.SaleProducts)) {
          requiredLineItems.push({
            id: id1,
            name,
            description,
            quantity,
            type: lineItemTypeId,
            price: price ? `$${price}` : "",
            total: total ? `$${total}` : "",
          });
          if (total) {
            totalRequired1 += Number(total);
          }
        }
      }
    }
  }
  if (!("functionName" in booking) && booking?.categories) {
    for (let i = 0; i < booking.categories.length; i++) {
      const category = booking.categories[i];
      const butlerProduct = findCategorizedProductById(butlerServiceGroups, category.productId);
      if (butlerProduct) {
        const productSize = find(productSizes, (p) => p.id === category.productSizeId);
        let price = butlerProduct.price ? Number(butlerProduct.price) : 0;
        const butlerServiceCategory = find(butlerServiceGroups, (bsc) => bsc._key === butlerProduct.categoryId);
        const butlerServiceCategoryName = butlerServiceCategory ? butlerServiceCategory.category : "";
        price = +price;
        let total = price * Number(category.quantity);
        total = +total;
        if (!butlerProduct.requiredPrepayment) {
          preorderLineItems.push({
            id: category.productSizeId,
            name: `${butlerProduct.productName}${productSize ? ` - ${productSize.name}` : ""}`,
            description: "",
            category: butlerServiceCategoryName,
            price: `$${price}`,
            quantity: category.quantity,
            type: 1,
            total: `$${total}`,
          });
          totalPreorder1 += price * Number(category.quantity);
        } else {
          requiredLineItems.push({
            id: category.productSizeId,
            name: `${butlerProduct.productName}${productSize ? ` - ${productSize.name}` : ""}`,
            description: "",
            quantity: category.quantity,
            category: butlerServiceCategoryName,
            type: 1,
            price: `$${price}`,
            total: price * Number(category.quantity),
          });
          totalRequired1 += Number(price * Number(category.quantity));
        }
      }
    }
  }
  if (paymentSummary?.lineItems) {
    for (let j = 0; j < paymentSummary.lineItems.length; j++) {
      const li = paymentSummary.lineItems[j];
      let total = 0;
      let price = 0;
      if (li.total) {
        total = li.total;
      }
      if (li.salesTax) {
        salesTax += Number(li.salesTax);
      }
      if (li.price) {
        price = li.price;
      }
      total = +Number(total);
      price = +Number(price);
      prepaidLineItems.push({
        id: li.id1,
        name: li.name,
        description: li.description,
        text: li.text,
        price,
        quantity: li.quantity,
        type: li.lineItemTypeId,
        total,
      });
    }
    if (paymentSummary.serviceFeeAmount) {
      serviceFee += Number(paymentSummary.serviceFeeAmount);
    }
    // if (booking.paymentSummary..surcharges) {
    //   surcharges = [...surcharges, ...transaction.surcharges];
    // }
    if (paymentSummary.serviceCharge) {
      serviceCharges.push(paymentSummary.serviceCharge);
    }
    totalPrepaid1 = Number(paymentSummary.grandTotal);
  }

  return [
    { items: preorderLineItems, total: `$${totalPreorder1}` },
    { items: prepaidLineItems, total: `$${totalPrepaid1}`, serviceFee: serviceFee, surcharges, serviceCharges, salesTax: salesTax },
    { items: requiredLineItems, total: `$${totalRequired1}` },
  ];
}

interface GetPaymentsFromBookingOnlyResponse {
  id: string;
  name: string;
  description: string;
  quantity: number;
  price?: number;
  total?: number;
  type: number;
}
export function getPaymentsFromBookingOnly(booking: Booking | FunctionBooking, products: Product[], menu: Menu, bookingTransactions: Transaction[] = []): [{ items: GetPaymentsFromBookingOnlyResponse[] }, { items: GetPaymentsFromBookingOnlyResponse[] }, { items: GetPaymentsFromBookingOnlyResponse[] }, boolean] {
  const preorderLineItems: GetPaymentsFromBookingOnlyResponse[] = [];
  const requiredLineItems: GetPaymentsFromBookingOnlyResponse[] = [];
  const prepaidLineItems: GetPaymentsFromBookingOnlyResponse[] = chain(bookingTransactions)
    .flatMap((t) => t.lineItems)
    .filter((li) => li.productCategoryId === ProductCategoryId.PrepaidMenus)
    .map(({ id, name, description, quantity, price, total }) => ({ id, name, description, quantity, price, total, type: 2 }))
    .value();
  let isPaid = false;
  const totalBookingPax = booking.pax + (booking.extraPax || 0);

  if (!("functionName" in booking) && menu?.menuPayments?.[booking.courseId]) {
    const mp = menu.menuPayments[booking.courseId];
    const isFullPaymentMenu = mp.paymentRequirementTypeId === PaymentRequirementTypes.FullPayment;
    const isCreditCardMenu = mp.paymentRequirementTypeId === PaymentRequirementTypes.CreditCardDetails;
    const isDepositMenu = mp.paymentRequirementTypeId === PaymentRequirementTypes.Deposit;

    let lineItems = [];
    if (isFullPaymentMenu) {
      const menuOptionIds = [booking.menuOptionId, ...map(booking.guests, (g) => g.menuOptionId)].filter((id) => id);
      const menuOptionIdCount = countBy(menuOptionIds);
      lineItems = map(menuOptionIdCount, (quantity, id) => {
        const product = products.find((p) => p.id === id);
        return { id: `full-payment-menu-${uniqueId()}`, name: `${product?.name} (${product?.fixedPriceMenuTypes.adult}) | Full Payment`, price: null, description: product?.description, quantity, type: 2, total: null };
      });
      isPaid = !booking.alg.onHold && !booking.alg.waitlist;
    } else if (isCreditCardMenu) {
      lineItems = [{ id: `credit-card-${uniqueId()}`, name: "Credit Card", description: "Credit Card", quantity: totalBookingPax, type: 2, price: null, total: null }];
      isPaid = booking.creditCardDetailsConfirmed && !booking.alg.onHold && !booking.ccOverride && !booking.alg.waitlist;
    } else if (isDepositMenu) {
      lineItems = [{ id: `deposit-menu-${uniqueId()}`, name: menu.name, description: menu.menuDescription, quantity: totalBookingPax, type: 2, price: null, total: null }];
      isPaid = !booking.alg.onHold && !booking.alg.waitlist;
    }
    if (!isPaid) requiredLineItems.push(...lineItems);

    if (booking?.categories) {
      for (let i = 0; i < booking.categories.length; i++) {
        const category = booking.categories[i];
        const item = {
          id: `butler-item-${category.productId}-${category.productSizeId}`,
          name: `${category.productName}${category.productSizeName ? ` - ${category.productSizeName}` : ""}`,
          description: (category.text || []).join(","),
          quantity: category.quantity,
          price: null,
          total: null,
          type: 1,
        };
        if (category.requiredPrepayment) prepaidLineItems.push(item);
        else preorderLineItems.push(item);
      }
    }
  }

  return [{ items: preorderLineItems }, { items: prepaidLineItems }, { items: requiredLineItems }, isPaid];
}
function getVisibleSubcategories(subcategories, order, widgetSubcategories = null) {
  const subcategories1 = chain(subcategories)
    .pickBy((_undefined, key) => !widgetSubcategories || (widgetSubcategories[key] && widgetSubcategories[key].enabled))
    .map((value, key) => ({ ...value, _key: key }))
    .value();
  return sortBy(subcategories1, (g) => order[g._key]);
}

function getVisibleCategories(categories, order) {
  let categories1 = pickBy(categories, (category) => category.enabled === true);
  categories1 = map(categories1, (value, key) => ({ ...value, _key: key }));
  return sortBy(categories1, (value) => order[value._key]);
}

function getVisibleProducts(products, order) {
  let products1 = pickBy(products, (product) => product.enabled === true);
  products1 = map(products1, (value, key) => ({ ...value, _key: key }));
  return sortBy(products1, (value) => order[value._key]);
}

export function isCurrentService(now: Moment, date: string, openingTimes: OpeningTimes) {
  return !isFutureService(now, date, openingTimes) && !isPastService(now, date, openingTimes);
}

export function isFutureService(now: Moment, date: string, openingTimes: OpeningTimes) {
  const now1 = now.clone();
  const isPast = isPastService(now1, date, openingTimes);
  if (isPast) return false;
  if (Number(date) === Number(now1.format("YYYYMMDD"))) {
    const ticks = now1.valueOf() - now1.startOf("day").valueOf();
    // if ticks is less than opening time then the service hasn't started yet
    return ticks < openingTimes.open;
  } else if (Number(moment(String(date), "YYYYMMDD").add(1, "days").format("YYYYMMDD")) === Number(now1.format("YYYYMMDD"))) {
    const ticks = now1.valueOf() - now1.startOf("day").valueOf();
    // if ticks is > than closing time last nights service has ended
    return ticks > openingTimes.close;
  }
  return Number(date) > Number(now1.format("YYYYMMDD"));
}

export function isPastService(now: Moment, date: string, openingTimes: OpeningTimes) {
  const now1 = momentTz(now);
  if (Number(date) === Number(now1.format("YYYYMMDD"))) {
    if (openingTimes && openingTimes.close && openingTimes.close > openingTimes.open) {
      const ticks = now1.valueOf() - now1.startOf("day").valueOf();
      return ticks > openingTimes.close;
    }
  } else if (Number(moment(String(date), "YYYYMMDD").add(1, "days").format("YYYYMMDD")) === Number(now1.format("YYYYMMDD"))) {
    if (openingTimes && openingTimes.close && openingTimes.close < openingTimes.open) {
      const ticks = now1.valueOf() - now1.startOf("day").valueOf();
      return ticks > openingTimes.close;
    }
  }
  return Number(date) < Number(now1.format("YYYYMMDD"));
}

export function getRule(rules: AllocationRules, groupAreaId: string): AllocationRule {
  const ruleId = findKey(rules, (rule) => rule.groupAreaId === groupAreaId);
  if (ruleId && rules[ruleId]) {
    return rules[ruleId];
  }
  return null;
}

export function isLocked(openingTimes: OpeningTimes, areaGroups: AreaGroup[], rules: AllocationRules, groupAreaId: string, now: Moment, date: string) {
  if (Number(now.format("YYYYMMDD")) > Number(date)) return true;
  const rule = getRule(rules, groupAreaId);
  const isToday = Number(now.format("YYYYMMDD")) === Number(date);
  return isToday && getLockedTime(openingTimes, rule) < 1000 * (3600 * now.hours() + 60 * now.minutes());
}

export function getLockedTime(openingTimes: OpeningTimes, rule: AllocationRule) {
  let lockBookings = 0;
  if (rule?.lockBookings && rule.lockBookingsValue) {
    lockBookings = rule.lockBookingsValue;
  }
  return openingTimes.open - lockBookings * 3600 * 1000;
}

export function getOpeningTimesAdjusted(open: number, close: number): { open: number; close: number } {
  let close1 = Number(close);
  if (close1 <= Number(open)) {
    close1 = 24 * 60 * 60 * 1000 + close1;
  }
  return { open: Number(open), close: close1 };
}

export function getButlerServiceGroups(butlerServiceCategories, products: Products, order, subcategories = null) {
  const butlerServiceCategories1 = getVisibleCategories(butlerServiceCategories, order);
  const groups = [];
  let index = 0;
  for (let i = 0; i < butlerServiceCategories1.length; i++) {
    const subcategories1 = getVisibleSubcategories(butlerServiceCategories1[i].subcategories, order, subcategories);
    if (subcategories1.length > 0) {
      groups.push({ _key: butlerServiceCategories1[i]._key, category: butlerServiceCategories1[i].name, subcategories: [], rank: order[butlerServiceCategories1[i]._key] });
      for (let j = 0; j < subcategories1.length; j++) {
        const products1 = {};
        for (const productId in products) {
          const subId = findKey(products[productId].butlerService, (butlerServiceItem: any) => butlerServiceItem.subcategoryId === subcategories1[j]._key);
          if (subId) {
            Object.assign(products1, { [productId]: products[productId] });
          }
        }
        if (Object.keys(products1).length > 0) {
          groups[index].subcategories.push({
            _key: subcategories1[j]._key,
            name: butlerServiceCategories1[i].subcategories[subcategories1[j]._key].name,
            rank: order[subcategories1[j]._key],
            products: getVisibleProducts(products1, order),
          });
        }
      }
      index++;
    }
  }
  return groups;
}

export function prepaymentCategories(categories, products: Products) {
  const results = [];
  if (categories) {
    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i];
      if (cat && cat.productId && products[cat.productId]) {
        if (products[cat.productId].butlerService) {
          if (cat.productSizeId && products[cat.productId].butlerService[cat.productSizeId] && products[cat.productId].butlerService[cat.productSizeId].requiredPrepayment) {
            results.push(cat);
          }
        }
      }
    }
  }
  return results;
}

export function checkBookingGuestSpace(booking, bookingCustomerEmail = null, emailsToCheck = []) {
  const guestPax = Number(booking.pax) - 1;
  const totalAdultGuest = guestPax - Number(booking.children || 0);
  const filledAdultGuest = (booking.guests || []).filter((g) => g.customerId && g.isAttending);
  const isAdultGuestSpaceAvailable = totalAdultGuest - filledAdultGuest.length > 0;
  const filledGuest = (booking.guests || []).filter((g) => g.isAttending);
  const isGuestSpaceAvailable = totalAdultGuest - filledAdultGuest.length > 0;
  let existingCustomerId = null;
  if (bookingCustomerEmail && emailsToCheck.includes(bookingCustomerEmail)) existingCustomerId = booking.customerId;
  emailsToCheck.forEach((email) => {
    const findGuest = filledAdultGuest.find((g) => g.customer.email === email);
    if (findGuest) {
      existingCustomerId = findGuest.customerId;
      return;
    }
  });

  return { totalAdultGuest, filledAdultGuest, isAdultGuestSpaceAvailable, totalGuest: guestPax, filledGuest, isGuestSpaceAvailable, existingCustomerId };
}

const isRealGuest = (g: Guest): boolean => Boolean(g.customerId || g.customer?.email || g.customer?.mobile);

export function getGuestsPrefilled(booking: Booking | FunctionBooking, adultMenuOptionId: string, childMenuOptionId: string): Guest[] {
  // guests excludes the booking customer, who is always an adult and counted in pax.
  // Placeholder slots ({ customerId: "" } with no details) are ignored.
  const guests = filter(booking.guests, isRealGuest);
  const existingAdults = filter(guests, (g) => !g.childOrDependent);
  const existingChildren = filter(guests, (g) => g.childOrDependent);

  const pax = Number(booking.pax) || 0;
  const extraPax = Number(booking.extraPax) || 0;
  const bookedChildren = Number(booking.children) || 0;
  const guestCount = pax - 1 + extraPax;

  // Never drop guests that have already been entered
  const totalGuests = Math.max(guestCount, guests.length);
  const childCount = Math.max(bookedChildren, existingChildren.length);
  const adultCount = Math.max(totalGuests - childCount, existingAdults.length);

  const buildSlots = (count: number, pool: Guest[], isChild: boolean): Guest[] => {
    const fallbackMenu = isChild ? childMenuOptionId || adultMenuOptionId : adultMenuOptionId;
    return Array.from({ length: count }, (_, i) => {
      const existing = pool[i];
      return {
        customerId: "",
        ...existing,
        customer: existing?.customer ?? { email: null, mobile: null, mobileCode: "AU" },
        childOrDependent: isChild,
        menuOptionId: existing?.menuOptionId || fallbackMenu,
      };
    });
  };

  return [...buildSlots(adultCount, existingAdults, false), ...buildSlots(childCount, existingChildren, true)];
}

export function getBookingExpiryTime(zoneId, booking) {
  const diffInTimeZone = momentTz().tz(zoneId).utcOffset() * 60;
  const startOfDay = momentTz.tz(booking.date, "YYYYMMDD", zoneId).unix() + diffInTimeZone;
  const endTime = booking.intervalId / 1000 + (booking.alg.duration || 0) * 60 - Number(booking.alg.reset || 0) * 60;
  const bookingExpiryTime = (startOfDay + endTime) * 1000 + 5 * 60 * 60 * 1000;
  return bookingExpiryTime;
}

export function getTabTransactionsData(tab: PaymentTab) {
  if (!tab) return { paidWithTabTransactions: [], totalTabAmount: 0, totalPaidAmount: 0 };
  const totalTabAmount = tab.amount;
  const paidWithTabTransactions = map(tab.tabTransactions, (t, _key) => ({ ...t, _key }));
  const totalPaidAmount = tab.amount - tab.remainingAmount;
  return { paidWithTabTransactions, totalTabAmount, totalPaidAmount };
}

export const getAvailableTabPayments = (tabs: PaymentTab[], customerId): AvailableTabPayment[] => {
  const ownedTabs: PaymentTab[] = tabs.filter((tab) => !tab.closed && tab.tabOwner === customerId);
  const associatedTabs: PaymentTab[] = tabs.filter((tab) => !tab.closed && (tab?.guests?.[customerId] || (tab?.allowAllGuests && tab.tabOwner !== customerId)));
  const availableTabPayments: AvailableTabPayment[] = [];
  ownedTabs.forEach((tab) => {
    const { totalTabAmount, totalPaidAmount, paidWithTabTransactions } = getTabTransactionsData(tab);
    const tabAvailableAmount = totalTabAmount - totalPaidAmount;
    const customerLimit = tab.tabOwnerLimit;
    const customerPaidAmount = paidWithTabTransactions.reduce((accumulator, transaction) => {
      const filterPayments = transaction.payments.filter((p) => (p.paymentTypeId === PaymentTypeId.Deposit || p.paymentTypeId === PaymentTypeId.Tab) && p.customerId === customerId);
      const totalPaymentByTab = filterPayments.reduce((acc, val) => acc + val.value, 0);
      return accumulator + totalPaymentByTab;
    }, 0);

    if (!customerLimit) {
      availableTabPayments.push({ tab, limit: null, availableAmount: tabAvailableAmount, paidAmount: customerPaidAmount });
    } else {
      const remainingAmount = customerLimit <= customerPaidAmount ? 0 : customerLimit;
      const availableAmount = remainingAmount <= tabAvailableAmount ? remainingAmount : tabAvailableAmount;
      availableTabPayments.push({ tab, limit: customerLimit, availableAmount, paidAmount: customerPaidAmount });
    }
  });
  associatedTabs.forEach((tab) => {
    const { totalTabAmount, totalPaidAmount, paidWithTabTransactions } = getTabTransactionsData(tab);
    const tabAvailableAmount = totalTabAmount - totalPaidAmount;
    const customerLimit = tab.guests?.[customerId]?.tabLimit || (tab.allowAllGuests ? (tab.perGuestLimit ? tab.perGuestLimit : tab.remainingAmount) : null);
    const customerPaidAmount = paidWithTabTransactions.reduce((accumulator, transaction) => {
      const filterPayments = transaction.payments.filter((p) => (p.paymentTypeId === PaymentTypeId.Deposit || p.paymentTypeId === PaymentTypeId.Tab) && p.customerId === customerId);
      const totalPaymentByTab = filterPayments.reduce((acc, val) => acc + val.value, 0);
      return accumulator + totalPaymentByTab;
    }, 0);

    if (!customerLimit) {
      availableTabPayments.push({ tab, limit: null, availableAmount: tabAvailableAmount, paidAmount: customerPaidAmount });
    } else {
      const remainingAmount = customerLimit <= customerPaidAmount ? 0 : customerLimit;
      const availableAmount = remainingAmount <= tabAvailableAmount ? remainingAmount : tabAvailableAmount;
      availableTabPayments.push({ tab, limit: customerLimit, availableAmount, paidAmount: customerPaidAmount });
    }
  });
  return availableTabPayments;
};

export const checkOrderItemExistsInDrinks = (menu: Menu, headingId: string) => {
  const groupHeadings = menu?.beverage?.groupHeadings || {};
  let found = values(groupHeadings).some((group) => group.menuHeadingId === headingId);
  if (found) return true;
  found = values(menu?.drinkInclusions).some(({ groupHeadings }) => values(groupHeadings).some((group) => group.menuHeadingId === headingId));
  return found;
};

export const checkOrderItemExistsInFoods = (menu: Menu, headingId: string) => {
  const groupHeadings = menu?.food?.groupHeadings || {};
  let found = values(groupHeadings).some((group) => group.menuHeadingId === headingId);
  if (found) return true;
  found = values(menu?.foodInclusions).some(({ groupHeadings }) => values(groupHeadings).some((group) => group.menuHeadingId === headingId));
  return found;
};

export const getOrderingAppLink = (widgetPath, date, restaurantId, mealId, bookingId, clientId, userId = null) => {
  const user = userId ? `&userId=${userId}` : "";
  return `${widgetPath}/ordering/welcome?date=${date}&restaurantId=${restaurantId}&mealId=${mealId}&bookingId=${bookingId}&clientId=${clientId}${user}`;
};

export const getBillAvailableAndUnpaidCustomerId = (bills: Bills, booking: Booking | FunctionBooking): [boolean, { [bookingId: string]: string[] }, { [functionId: string]: string[] }] => {
  const unpaidBookingCustomerIds: { [bookingId: string]: string[] } = {};
  const unpaidFunctionBookingCustomerIds: { [functionId: string]: string[] } = {};
  let filteredBills = bills;
  if (booking && !("functionName" in booking)) {
    filteredBills = pickBy(bills, (pl) => pl.bookingId === booking._key);
  } else if (booking && "functionName" in booking) {
    filteredBills = pickBy(bills, (pl) => pl.functionId === booking._key);
  }
  for (const pl in filteredBills) {
    if (filteredBills[pl].bookingId) {
      unpaidBookingCustomerIds[filteredBills[pl].bookingId] = unpaidBookingCustomerIds[filteredBills[pl].bookingId] || [];
      unpaidBookingCustomerIds[filteredBills[pl].bookingId].push(filteredBills[pl].customerId);
    } else if (filteredBills[pl].functionId) {
      unpaidFunctionBookingCustomerIds[filteredBills[pl].functionId] = unpaidBookingCustomerIds[filteredBills[pl].functionId] || [];
      unpaidFunctionBookingCustomerIds[filteredBills[pl].functionId].push(filteredBills[pl].customerId);
    }
  }
  return [keys(filteredBills).length > 0, unpaidBookingCustomerIds, unpaidFunctionBookingCustomerIds];
};

export const getNextCourseOrderIndex = (sortedFixedOrderBatchSettings: BatchSetting[], sentOrderItems: OrderItem[], bookingBuckets: Buckets) => {
  let nextCourseIndex = -1;
  const courseGroups: CourseGroup[] = [];
  const allPendingBuckets: Bucket[] = [];
  const buckets = filter(bookingBuckets, (bucket) => !bucket.isCasual);
  let preparedCourseFound = false;
  sortedFixedOrderBatchSettings.forEach((batchSetting) => {
    const sortedCourseGroups: CourseGroup[] = orderBy(batchSetting.courseGroups, ["order"], ["asc"]);
    const isBatchItemSent: boolean = sentOrderItems.find((soi) => batchSetting.menuHeadingIds.includes(soi.headingId)) !== undefined;
    const pendingBucket: Bucket = buckets.find((bucket) => bucket.batchSettingId === batchSetting.id && !bucket.completed);
    if (pendingBucket) allPendingBuckets.push(pendingBucket);
    const courseGroupsLength: number = courseGroups.length;
    courseGroups.push(...sortedCourseGroups.map((cg) => ({ ...cg, batchSettingId: batchSetting.id })));
    if (isBatchItemSent) {
      for (let i = courseGroupsLength; i < courseGroups.length; i++) {
        const courseGroup = courseGroups[i];
        const isCourseItem = sentOrderItems.find((soi) => courseGroup.menuHeadingIds.includes(soi.headingId)) !== undefined;
        if (isCourseItem) {
          if (preparedCourseFound) {
            nextCourseIndex = i;
            preparedCourseFound = false;
          }
          const sentCourseItems = sentOrderItems.filter((aoi) => courseGroup.menuHeadingIds.includes(aoi.headingId));
          const allItemsPrepared = sentCourseItems.every((oi) => oi.orderStatus >= OrderItemStatus.Prepared);
          if (allItemsPrepared) {
            preparedCourseFound = true;
            nextCourseIndex = -1;
          }
        }
      }
    }
  });

  const previousBatchSettingIds: Array<string> = [];
  for (let i = 0; i < nextCourseIndex; i++) courseGroups.length > 0 && previousBatchSettingIds.push(courseGroups[i].batchSettingId);
  const previousPendingBuckets: Bucket[] = allPendingBuckets.filter((bucket) => previousBatchSettingIds.includes(bucket.batchSettingId));
  return [nextCourseIndex, courseGroups, previousPendingBuckets] as [number, CourseGroup[], Bucket[]];
};

export const GetServiceAmount = (totalAmount: number, paymentTypeId: PaymentTypeId, fees: OnlinePayments | EftposPayments | BankTransferFee) => {
  const { serviceFee = 0 } = fees || {};
  let serviceFeeAmount = 0;
  if ([PaymentTypeId.Eftpos, PaymentTypeId.Offline, PaymentTypeId.CNP].includes(paymentTypeId)) {
    serviceFeeAmount = (serviceFee / 100) * totalAmount;
  } else if ([PaymentTypeId.BankTransfer].includes(paymentTypeId)) {
    serviceFeeAmount = (serviceFee / 100) * totalAmount;
    const resbutlerFeeAmount = "resbutlerFeeAmount" in fees ? fees.resbutlerFeeAmount || 0 : 0;
    if ("serviceFeeMaxAmount" in fees && fees.serviceFeeMaxAmount > 0 && serviceFeeAmount > fees.serviceFeeMaxAmount) return fees.serviceFeeMaxAmount + resbutlerFeeAmount;
    else serviceFeeAmount = serviceFeeAmount + resbutlerFeeAmount;
  }
  return MoneyUtils.round(serviceFeeAmount);
};

export function buildDataFromDocs<T>(docs): T {
  const data = {} as T;
  if (!docs) return data;
  docs.forEach((doc) => {
    data[doc.id] = doc.data();
  });
  return data;
}

export function buildDataFromCollection<T>(collectionData): T {
  let docData = {} as T;
  if (collectionData.empty || isEmpty(collectionData)) return docData;
  collectionData.docs.forEach((doc) => {
    docData = Object.assign(docData, { [doc.id]: doc.data() });
  });
  return docData;
}

export function buildArrayFromDocs<T>(docs) {
  const data: T[] = [];
  if (!docs) return data;
  docs.forEach((doc) => {
    data.push({ ...doc.data(), id: doc.id } as T);
  });
  return data;
}

// Helper to convert objects like Customers to Customer[]
export function toArrayWithIds<T>(docs: { [id: string]: T }): (T & { id: string })[] {
  return compact(map(docs, (doc, id) => (doc ? { ...doc, id } : null)));
}

export function getShardsFromMaestro(m: Maestro | MaestroDate): string[] {
  // Convert data to Buffer if it's a string
  const jsonString = typeof m === "string" ? m : JSON.stringify(m);

  const characters = jsonString.split("");
  const resultArray: string[] = [];
  const numShards = 20 * 1024; // 20KB per shard

  for (let i = 0; i < characters.length; i += numShards) {
    resultArray.push(characters.slice(i, i + numShards).join(""));
  }
  return resultArray;
}

export function getMaestroFromShards<T extends Maestro | MaestroDate>(shards: { data: string; index: number }[]): T {
  if (shards.length < 1) {
    return null; // Invalid input
  }

  return JSON.parse(
    chain(shards)
      .orderBy((s) => s.index)
      .map((s) => s.data)
      .value()
      .join("")
  );
}

export function findGroupHeadingByMenu(menu: Menu, menuOptionId: string, targetId: string): GroupHeading | null {
  const groupHeadings: GroupHeadings = assign({}, menu.food?.groupHeadings, menu.beverage?.groupHeadings, menu.drinkInclusions?.[menuOptionId]?.groupHeadings, menu.foodInclusions?.[menuOptionId]?.groupHeadings);
  return findGroupHeadingById(groupHeadings, targetId);
}

export function findGroupHeadingById(groupHeadings: GroupHeadings, targetId: string): GroupHeading | null {
  if (!groupHeadings) return null;
  if (groupHeadings[targetId]) return groupHeadings[targetId];

  for (const key in groupHeadings) {
    const childGroup = groupHeadings[key];
    const result = findGroupHeadingById(childGroup.groupHeadings, targetId);
    if (result) {
      return result;
    }
  }
  return null;
}

export const getAttendingGuestsOfBooking = (booking: Booking | FunctionBooking): Guest[] => {
  if (!booking) return [];
  let guestsOfBooking = [];
  if (booking.guests) {
    guestsOfBooking = booking.guests.filter((g) => g.isAttending && g.customerId);
  }
  return guestsOfBooking;
};

export function getCustomerNameByNameDisplayConfig(restaurant: Restaurant, customer: Customer | Partial<Customer>) {
  if (customer?.firstName && customer?.lastName) {
    if (restaurant?.customerNameDisplay === "firstName") {
      return `${customer.firstName} ${customer.lastName}`;
    }
    return `${customer.lastName}, ${customer.firstName}`;
  }
  return "";
}

export function getCustomerOrGuestDisplayName(customerId: string, restaurant: Restaurant, linkedBooking: Booking, customers: Customers) {
  if (!customerId || !linkedBooking) return "";
  const guestsOfBooking = getAttendingGuestsOfBooking(linkedBooking);
  const guest = guestsOfBooking.find((g) => g.customerId === customerId);

  const name = getCustomerNameByNameDisplayConfig(restaurant, guest?.customer || customers[customerId]);
  const seat = guest?.seat || linkedBooking?.organizerSeat;

  const position = seat ? `Position : ${seat} ${name ? ", " : ""}` : "";
  return `${position} ${name}`;
}

/**
 * Checks if given Menu contains any one of PaymentRequirementTypes
 * @return boolean
 */
export function hasPaymentType(menu: Menu, paymentTypes: PaymentRequirementTypes[]): boolean {
  if (!menu) return false;
  return findKey(menu?.menuPayments, (mp) => includes(paymentTypes, mp.paymentRequirementTypeId)) !== undefined;
}

/**
 *
 * @param value value in number or string
 * @return boolean which represents whether it can be valid number or not
 */
export function canThisBeNumber(value: number | string): boolean {
  const valueToNumber = value || value === "0" || value === 0 ? Number(value) : null;
  if (valueToNumber || valueToNumber === 0) return true;
  return false;
}

/**
 *
 * @param zoneId
 * @returns number (millis from midnight)
 * @description For NON React compiler app like Client Ordering App
 * @deprecated use getWithEpochNowByZoneId going forward
 */
export const getNowByZoneId = (zoneId: string) => {
  const now = momentTz.tz(zoneId);
  return (now.hour() * 3600 + now.minute() * 60) * 1000;
};

/**
 * @param zoneId
 * @returns number (milliseconds from midnight)
 * @description For NON React compiler app like Client Ordering App.
 * @deprecated use getTodayWithEpochMsByZoneId going forward
 */
export const getTodayByZoneId = (zoneId: string): number => {
  console.log(`++ getTodayByZoneId() -> zoneId is "${zoneId ?? "zoneId is NOT known"}"`);
  return Number(moment.tz(moment(), zoneId ?? SYDNEY_TZ).format("YYYYMMDD"));
};

/**
 *
 * @param nowEpochMs
 * @param zoneId
 * @returns number (milliseconds from midnight)
 * @description For React compiler app like Resbutler web
 * @example
 *    getWithEpochNowByZoneId(Date.now(), zoneId)
 *
 */
export const getWithEpochNowByZoneId = (nowEpochMs: number, zoneId: string = SYDNEY_TZ): number => {
  console.log(`++ getWithEpochNowByZoneId() -> nowEpochMs is "${nowEpochMs}"`);
  const now = momentTz.tz(nowEpochMs, zoneId);
  return (now.hours() * 60 + now.minutes()) * 60_000;
};

/**
 *
 * @param nowEpochMs
 * @param zoneId
 * @returns number (milli from midnight)
 * @description For React compiler app like Resbutler web.
 * @example getTodayWithEpochMsByZoneId(Date.now(), zoneId))
 */
export const getTodayWithEpochMsByZoneId = (nowEpochMs: number, zoneId: string = SYDNEY_TZ): number => {
  console.log(`++ getTodayWithEpochMsByZoneId() -> nowEpochMs is "${nowEpochMs}"`);
  return Number(momentTz.tz(nowEpochMs, zoneId).format("YYYYMMDD"));
};

export function arePhoneNumbersEqual(phoneNumber1: string, phoneNumber2: string): boolean {
  const strippedNumber1 = phoneNumber1.replace(/^\+/, "");
  const strippedNumber2 = phoneNumber2.replace(/^\+/, "");
  return strippedNumber1 === strippedNumber2;
}

export function doesPhoneNumberExist(phoneNumbers: string[], numberToCheck: string): boolean {
  return some(phoneNumbers, (phoneNumber) => {
    return arePhoneNumbersEqual(phoneNumber, numberToCheck) || phoneNumber === numberToCheck;
  });
}

/**
 * Picks unique item based on given path and picks the one with max value between them
 * Use case: get the recently used unique items
 */
export function uniqueAndRecentBy<T>(list: T[], groupPath: string, maxByField: string): T[] {
  return chain(list)
    .groupBy(groupPath)
    .mapValues((item) => maxBy(item, maxByField))
    .values()
    .value();
}

/**
 * Filters given list of order by bookingId, mealId, customterId
 */
// const filterOrderByBookingId = (bills: Bills, bookingId?: string, customerId?: string): Bills => {
//   if (bookingId && customerId) {
//     return pickBy(bills, (pl) => pl.bookingId === bookingId && pl.customerId === customerId);
//   } else if (bookingId && !customerId) {
//     return pickBy(bills, (pl) => pl.bookingId === bookingId);
//   } else if (!bookingId) {
//     return pickBy(bills, (pl) => pl.customerId === customerId);
//   }
//   return bills;
// };

/**
 * Iterates over given list of orders and returns matches based on given predicate on OrderItem
 */
// const getFilteredOrderStatus = (orders: Dictionary<Order>, predicate: (oi: OrderItem) => boolean, bookingId?: string, mealId?: string, customerId?: string) => {
//   let hasMatchedOrders = false;
//   const matchedBookingIds: { [bookingId: string]: string[] } = {};

//   forEach(orders, (o) => {
//     forEach(o.orderItems, (oi) => {
//       if (predicate(oi)) {
//         if (!matchedBookingIds[o.bookingId]) matchedBookingIds[o.bookingId] = [];
//         hasMatchedOrders = true;
//         if (!matchedBookingIds[o.bookingId].includes(o.customerId)) matchedBookingIds[o.bookingId].push(o.customerId);
//       }
//     });
//   });

//   return { hasMatchedOrders, matchedBookingIds };
// };

/**
 * Return status of Bills (Pay Later orders) to be paid now
 *
 */
export const getPayBillNowStatus = (bills: Bills, booking: Booking | FunctionBooking, customerId = ""): [boolean, { [bookingId: string]: string[] }, boolean, { [functionId: string]: string[] }] => {
  let filteredPayNow: Bills = {};
  const key = booking && "functionName" in booking ? "functionId" : "bookingId";
  if (booking && customerId) {
    filteredPayNow = pickBy(bills, (pl) => pl[key] === booking._key && pl.customerId === customerId && findKey(pl.orderItems, (oi) => oi.payNow && oi.payLater) !== undefined);
  } else if (booking && !customerId) {
    filteredPayNow = pickBy(bills, (pl) => pl[key] === booking._key && findKey(pl.orderItems, (oi) => oi.payNow && oi.payLater) !== undefined);
  } else {
    filteredPayNow = pickBy(bills, (pl) => findKey(pl.orderItems, (oi) => oi.payNow && oi.payLater) !== undefined);
  }
  const matchedBookingIds: { [bookingId: string]: string[] } = {};
  const matchedFunctionIds: { [functionId: string]: string[] } = {};
  for (const pl in filteredPayNow) {
    if (filteredPayNow[pl].bookingId) {
      matchedBookingIds[filteredPayNow[pl].bookingId] = matchedBookingIds[filteredPayNow[pl].bookingId] || [];
      matchedBookingIds[filteredPayNow[pl].bookingId].push(filteredPayNow[pl].customerId);
    } else if (filteredPayNow[pl].functionId) {
      matchedFunctionIds[filteredPayNow[pl].functionId] = matchedFunctionIds[filteredPayNow[pl].functionId] || [];
      matchedFunctionIds[filteredPayNow[pl].functionId].push(filteredPayNow[pl].customerId);
    }
  }
  return [keys(matchedBookingIds).length > 0, matchedBookingIds, keys(matchedFunctionIds).length > 0, matchedFunctionIds];
};

// a simple implementation of the shallowCompare.
// only compares the first level properties and hence shallow.
// state updates(theoretically) if this function returns true.
export function shallowCompare(newObj: any, prevObj: any) {
  for (const key in newObj) {
    if (newObj[key] !== prevObj[key]) return true;
  }
  return false;
}

/**
 * Returns status of future dates for given start date and optional end date in date label
 * End date defaults to end of the month if not specified
 * @param {Label} labels Date label
 * @param {string} zoneId zoneId for date/ time calculation. usually restaurant's zone id, eg "Sydney/ Australia"
 * @param {string | number} startDate start date value
 * @param {string | number} endtDate optional end date value
 */
export function getDateStatusByDateRange(labels: Labels, restaurantId: string, zoneId: string, startDate: string | number, maxDays?: number) {
  const currentDate = momentTz.tz(startDate, zoneId);
  const maxDate = maxDays ? currentDate.clone().add(maxDays, "days") : momentTz.tz(startDate, zoneId).endOf("month");

  const dateStatus: { [date: number]: boolean } = {};

  while (currentDate.isSameOrBefore(maxDate, "day")) {
    const dateLabelId = getDateLabelId(labels, zoneId, currentDate, restaurantId);
    if (dateLabelId) {
      const dateLabel = labels[dateLabelId];
      dateStatus[Number(currentDate.format("YYYYMMDD"))] = isClosed(dateLabel, zoneId, currentDate);
    } else {
      dateStatus[Number(currentDate.format("YYYYMMDD"))] = true;
    }
    currentDate.add(1, "day");
  }
  return dateStatus;
}

/**  @deprecated use parsePhoneNumber instead */
export function isValidMobileNumberForCountry(phoneNumberString, mobileCode) {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const phoneNumber = phoneUtil.parseAndKeepRawInput(phoneNumberString, mobileCode);
    return !(phoneUtil.getNumberType(phoneNumber) !== PhoneNumberType.MOBILE && phoneUtil.getNumberType(phoneNumber) !== PhoneNumberType.FIXED_LINE_OR_MOBILE);
  } catch (error) {
    console.log(error.message);
    return false;
  }
}

/** @deprecated use parsePhoneNumber instead */
export function isValidPhoneNumberForCountry(phoneNumberString, countryCode) {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const phoneNumber = phoneUtil.parseAndKeepRawInput(phoneNumberString, countryCode);
    return phoneUtil.isValidNumberForRegion(phoneNumber, countryCode);
  } catch (error) {
    console.log(error.message);
    return false;
  }
}

export function isValidEmail(email: string): boolean {
  if (typeof email !== "string") return false;
  if (email.length === 0 || email.length > 254) return false;

  const parts = email.split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;

  // Part before @: 1–64 chars, allowed characters, dots only between other characters
  if (local.length === 0 || local.length > 64) return false;
  if (!/^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/.test(local)) return false;

  // Domain: at least two labels, each 1–63 chars, no leading/trailing hyphen
  if (domain.length > 253) return false;
  const labels = domain.split(".");
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (label.length === 0 || label.length > 63) return false;
    if (!/^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(label)) return false;
  }

  // TLD: letters only (e.g. com, museum) or punycode (e.g. xn--p1ai)
  const tld = labels[labels.length - 1];
  return /^(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9-]{1,59})$/.test(tld);
}

/**
 * Generic utility to extract given propertyName from given object
 * @param object
 * @param propertyName
 * @returns
 */
export function extractProperty<T, R>(object: { [id: string]: T }, propertyName: keyof T): R[] {
  const result = chain(object)
    .map((value) => get(value, propertyName))
    .filter(identity)
    .value();

  return result as R[];
}

export function rejectAfterDelay(ms) {
  return new Promise((_, reject) => {
    setTimeout(reject, ms, new Error("timeout"));
  });
}

export function getDateFromNow(now: Moment): string {
  const ticks = getTicks(now);
  let date = null;
  if (ticks < 5 * 60 * 60 * 1000) {
    date = moment(now).subtract(1, "day").format("YYYYMMDD");
  } else {
    date = now.format("YYYYMMDD");
  }
  return date;
}

/** Find current date and mealId for restaurant */
export function getCurrentMaestro(seatings: Seatings, date: string, restaurantId: string, now: Moment, zoneId: string): { date: string; restaurantId: string; mealId: string } {
  "use no memo";
  let ticks;
  let getLastSeating = false;
  if (!date) {
    // const now = moment.tz(zoneId);
    ticks = getTicks(now);
    if (ticks < 5 * 60 * 60 * 1000) {
      date = moment(now).subtract(1, "day").format("YYYYMMDD");
      getLastSeating = true;
    } else {
      date = moment(now).format("YYYYMMDD");
    }
  }
  let lastSeatingsForTick: Seatings = {};
  if (getLastSeating) {
    lastSeatingsForTick = pickBy(seatings, (seating) => isTickInBetween(seating.open, seating.close, ticks + 24 * 60 * 60 * 1000));
    if (keys(lastSeatingsForTick).length === 0) {
      const seatingsArray = toPairs(seatings);
      const maxCloseSeatings = maxBy(seatingsArray, ([, value]) => value.close);
      if (maxCloseSeatings) {
        lastSeatingsForTick = fromPairs([maxCloseSeatings]);
      }
    }
  }
  const filteredSeatings = keys(lastSeatingsForTick).length ? lastSeatingsForTick : seatings;
  const seatings1 = chain(filteredSeatings).values().orderBy(["open"]).value();
  // const now = momentTz.tz(zoneId);
  const date1 = momentTz.tz(date, "YYYYMMDD", zoneId);
  const isToday = now.isSame(date1, "day");
  if (isToday === true) {
    for (let i = 0; i < seatings1.length; i++) {
      const seating = seatings1[i];
      const start = (now.hour() * 3600 + now.minute() * 60) * 1000;
      const intervalId = getIntervalIdByTicks(seating, start);
      if (intervalId) {
        return { date: date1.format("YYYYMMDD"), restaurantId, mealId: seating.mealId };
      }
    }
  } else {
    for (let i = 0; i < seatings1.length; i++) {
      const seating = seatings1[i];
      const intervalId = getIntervalIdByTicks(seating, 0);
      if (intervalId) {
        return { date: date1.format("YYYYMMDD"), restaurantId, mealId: seating.mealId };
      }
    }
  }
  return { date: date1.format("YYYYMMDD"), restaurantId, mealId: "" };
}

/**
 * Helper to parse given phone number
 * @param phoneNumber
 * @param supportedCountryCodes
 * @returns
 */
export function parsePhoneNumber(phoneNumber: string, supportedCountryCodes: string[]): ParsedPhone {
  const phoneUtil = PhoneNumberUtil.getInstance();

  try {
    const number = phoneUtil.parse(phoneNumber);
    const numberType = phoneUtil.getNumberType(number);
    const isSupportedPhone = supportedCountryCodes.some((code) => phoneUtil.isValidNumberForRegion(number, code));
    const isMobileNumber = [PhoneNumberType.MOBILE, PhoneNumberType.FIXED_LINE_OR_MOBILE].includes(numberType);
    return {
      type: phoneUtil.getNumberType(number),
      countryCode: number.getCountryCode(),
      formatted: phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL),
      isMobileNumber,
      isSupportedPhone,
      isSupportedMobile: isMobileNumber && isSupportedPhone,
    };
  } catch (error) {
    console.error(error.message);
    return null;
  }
}

export function string_to_slug(str): string {
  str = str.replace(/^\s+|\s+$/g, ""); // trim
  str = str.toLowerCase();

  // remove accents, swap ñ for n, etc
  const from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;";
  const to = "aaaaeeeeiiiioooouuuunc------";
  for (let i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  str = str
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // collapse whitespace and replace by -
    .replace(/-+/g, "-"); // collapse dashes

  return str;
}

export function getAllBookingTables(booking: Booking | FunctionBooking) {
  const tables: { [tableId: string]: number } = {};
  Object.assign(tables, booking.alg?.tables);
  if (booking.alg?.proximity) {
    for (let i = 0; i < booking.alg.proximity.length; i++) {
      Object.assign(tables, booking.alg.proximity[i].tables);
    }
  }
  return tables;
}

export function getAllBookingSections(booking: Booking | FunctionBooking): { [sectionId: string]: boolean } {
  const sections: { [sectionId: string]: boolean } = {};
  if (booking.alg?.sectionId) sections[booking.alg.sectionId] = true;
  if (booking.alg?.proximity) {
    for (let i = 0; i < booking.alg.proximity.length; i++) {
      if (booking.alg.proximity[i]?.sectionId) {
        sections[booking.alg.proximity[i].sectionId] = true;
      }
    }
  }
  return sections;
}

/**
 * Remove props (inc nested) with null values.
 * Updated to handle array
 */
export function removePropsWithNullValues<T>(obj: T): Partial<T> {
  if (obj === null || obj === undefined) {
    return obj as any;
  }

  // Handle arrays - iterate and clean each element
  if (Array.isArray(obj)) {
    return obj.map((item) => removePropsWithNullValues(item)) as any;
  }

  if (isObject(obj)) {
    return transform(
      obj,
      (result: any, value: any, key: string) => {
        // Skip undefined and null values
        if (value === undefined || value === null) {
          return;
        }

        // Handle arrays - preserve as arrays
        if (Array.isArray(value)) {
          result[key] = value.map((item) => removePropsWithNullValues(item));
          return;
        }

        // Handle nested objects
        if (isObject(value)) {
          const cleanValue = removePropsWithNullValues(value);
          // Only include non-empty objects
          if (Object.keys(cleanValue).length > 0) {
            result[key] = cleanValue;
          }
          return;
        }

        result[key] = value;
      },
      {} as Partial<T>
    );
  }

  return obj;
}

export function stringFormat(template, ...args) {
  return template.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != "undefined" ? args[number] : match;
  });
}

/**
 * Checks if two objects have the same keys.
 * This does not check for value equality of nested objects.
 * @param {Object} obj1 - The first object to compare.
 * @param {Object} obj2 - The second object to compare.
 * @returns {boolean} - Returns true if both objects have the same keys, otherwise false.
 */
export function haveSameKeys(obj1, obj2) {
  return isEqual(keys(obj1).sort(), keys(obj2).sort());
}

export const generateUUID = (sample?: string): string => {
  if (!sample) {
    const hex = times(5, (i) => {
      const size = i === 2 ? 3 : 4;
      return times(size, () => random(15).toString(16)).join("");
    });

    return `${hex[0]}-${hex[1]}-5${hex[2]}-${random(8, 11).toString(16)}${hex[3]}-${hex[4]}`;
  }

  // generate consistent UUID 5 based on sample string
  const num = chain(sample)
    .replace(/\D/g, "")
    .padEnd(20, "0")
    .split("")
    .chunk(4)
    .map((p) => parseInt(join(p, ""), 10).toString(16).padStart(4, "0"))
    .value();

  return `${num[0]}-${num[1]}-5${num[2].slice(1)}-${((parseInt(num[3][0], 16) & 0x3) | 0x8).toString(16)}${num[3].slice(1)}-${num[4]}`;
};

export function isFunctionBooking(booking: Booking | FunctionBooking): booking is FunctionBooking {
  if (!booking) return false;
  return "functionName" in booking;
}

export const getUser = (booking: Booking | FunctionBooking, customer: Customer, userId: string) => {
  if (customer && booking?.customerId === userId) return customer;
  if (booking && customer) {
    const guest = find(booking.guests, (user) => user.customerId === userId);
    if (guest?.customer) {
      return guest.customer;
    }
    return customer;
  }
  let guest = find(booking?.guests, (g) => g.customerId === userId);
  if (!guest && !isFunctionBooking(booking)) guest = find((booking as Booking)?.notAttending, (g) => g.customerId === userId);
  if (guest) return guest.customer;
  return null;
};

/**
 * Generates a random numeric value with the specified number of digits, min digit is 4 , default is 6
 */
export const generateRandomNumber = (digits = 6): number => {
  const length = Math.max(4, digits);
  const min = 10 ** (length - 1);
  const max = 10 ** length - 1;

  return Math.floor(min + Math.random() * (max - min + 1));
};

export const isInPast = (timestamp: firebase.firestore.Timestamp, zoneId: string, granularity: unitOfTime.StartOf = "day") => {
  if (!timestamp || typeof timestamp !== "object" || !("_seconds" in timestamp || "seconds" in timestamp) || !("_nanoseconds" in timestamp || "nanoseconds" in timestamp)) {
    throw new Error("Invalid timestamp object. Expected an object with seconds and nanoseconds properties");
  }

  const timestampMillis = timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000);
  const now = momentTz.tz(zoneId);
  const timestampMoment = momentTz.tz(timestampMillis, zoneId);

  return timestampMoment.isBefore(now, granularity);
};

export function getMergedSeatings(
  seatings: Seatings,
  maestroSeatings:
    | Partial<Seatings>
    | {
        [seatingId: string]: "disabled";
      }
): Seatings {
  const deletedSeatings = pickBy(maestroSeatings, (s) => s === "disabled");
  return merge(
    {},
    pickBy(seatings, (s, k) => !deletedSeatings[k]),
    pickBy(maestroSeatings, (s) => s !== "disabled")
  );
}

/**
 * Converts a BigQuery-style timestamp field (an object with a `.value` string,
 * or any Date-parsable value (string, number, Date) into epoch milliseconds.
 *
 * @example
 * bigQueryTimestampToMs({ value: "2024-01-15T10:30:00Z" }); // 1705314600000
 * bigQueryTimestampToMs("2024-01-15T10:30:00Z");             // 1705314600000
 * bigQueryTimestampToMs(undefined);                          // undefined
 */
export const bigQueryTimestampToMs = (ts: any): number | undefined => (ts ? new Date(ts.value ?? ts).getTime() : undefined);

/**
 * Converts a Firestore-style timestamp object into epoch milliseconds.
 * Supports both client SDK shape (`seconds`, `nanoseconds`) and admin/raw shape (`_seconds`, `_nanoseconds`)
 *
 * @example
 * toMilliseconds({ seconds: 1705314600, nanoseconds: 500000000 }); // 1705314600500
 * toMilliseconds({ _seconds: 1705314600, _nanoseconds: 0 });       // 1705314600000
 * toMilliseconds(null); // null
 */
export const toMilliseconds = (timestamp: any): number | null => {
  if (!timestamp || typeof timestamp !== "object") {
    // throw new Error("Invalid timestamp object. Expected an object with seconds and nanoseconds properties");
    return null;
  }
  const seconds = ("_seconds" in timestamp ? timestamp._seconds : timestamp.seconds) as number;
  const nanoseconds = ("_nanoseconds" in timestamp ? timestamp._nanoseconds : timestamp.nanoseconds) as number;
  return seconds * 1000 + Math.floor(nanoseconds / 1000000);
};

export const isSuspiciousCustomerId = (id: string): boolean => /^\d{6,10}$/.test(id);

export function base64Encode(obj: any): string {
  if (obj === undefined) {
    console.error("Cannot encode undefined value");
    return "";
  }

  try {
    const jsonString = JSON.stringify(obj);
    return btoa(jsonString);
  } catch (error) {
    console.error(`Failed to encode object: ${(error as Error).message}`);
    return "";
  }
}

export function base64Decode(base64String: string) {
  if (typeof base64String !== "string") {
    console.error("Input must be a string");
  }

  if (base64String.trim() === "") {
    console.error("Cannot decode empty string");
  }

  try {
    const jsonString = atob(base64String);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error(`Failed to decode string: ${(error as Error).message}`);
  }
}

// web app use globalThis.crypto is the Web Crypto API
// Backend Node.js ≤18: globalThis.crypto is undefined, fall back to crypto.webcrypto from the Node built-in
const webCrypto: Crypto = globalThis.crypto ?? (crypto as any).webcrypto;

async function deriveKey(passphrase: string) {
  const enc = new TextEncoder().encode(passphrase);
  const hash = await webCrypto.subtle.digest("SHA-256", enc);
  return webCrypto.subtle.importKey("raw", hash, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

// Encrypts given object with passphrase and returns URL-safe base64 string
export async function encrypt(obj: any, passphrase: string, type: "simple" | "strong" = "simple"): Promise<string> {
  const text = JSON.stringify(obj);
  const encoder = new TextEncoder();
  const data = encoder.encode(text);

  if (type === "simple") {
    const key = await deriveKey(passphrase);
    const iv = new Uint8Array(12); // fixed IV

    const encrypted = await webCrypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);

    const bytes = new Uint8Array(encrypted);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  const salt = webCrypto.getRandomValues(new Uint8Array(16));
  const iv = webCrypto.getRandomValues(new Uint8Array(12));

  const keyMaterial = await webCrypto.subtle.importKey("raw", encoder.encode(passphrase), { name: "PBKDF2" }, false, ["deriveKey"]);

  const key = await webCrypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );

  const encrypted = await webCrypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);

  const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encrypted), salt.length + iv.length);

  return btoa(String.fromCharCode.apply(null, Array.from(combined)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

// decrypts string back to its original object of expected type T (tries simple first, then strong)
export async function decrypt<T = any>(encrypted: string, passphrase: string): Promise<T> {
  // first try simple
  try {
    const bytes = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));
    const key = await deriveKey(passphrase);
    const iv = new Uint8Array(12);

    const decrypted = await webCrypto.subtle.decrypt({ name: "AES-GCM", iv }, key, bytes);
    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch {
    // fallback to strong
  }

  // convert URL-safe base64 back
  let base64 = encrypted.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  const combined = new Uint8Array(Array.from(atob(base64)).map((c) => c.charCodeAt(0)));

  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 28);
  const ciphertext = combined.slice(28);

  const encoder = new TextEncoder();
  const keyMaterial = await webCrypto.subtle.importKey("raw", encoder.encode(passphrase), { name: "PBKDF2" }, false, ["deriveKey"]);

  const key = await webCrypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );

  const decrypted = await webCrypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
  return JSON.parse(new TextDecoder().decode(decrypted));
}

// is given given text Firesore Doc Id like string ?
export const isFirestoreDocId = (text: string) => {
  // not a Firestore doc ID if contains spaces (multiple words)
  if (text.includes(" ")) {
    return false;
  }
  // text for 20+ chars, alphanumeric + underscore without space and hyphen only at start
  return /^-?[A-Za-z0-9_]{20,}$/.test(text);
};

export function hasStringKeyValue(obj) {
  if (isString(obj)) {
    return true; // Found a string value
  }

  if (isObject(obj)) {
    return some(obj, (value) => hasStringKeyValue(value)); // Recursively check nested objects
  }

  return false; // Not a string value and not an object
}

/**
 * Since date values seems to vary a lot, this helper will return a consistent value
 * moving forward we should stick to firebase.firestore.Timestamp
 * instead of sometimes DD/MM/YYYY string, or moment Date, or Date or even admin.firestore.Timestamp.
 */
export function parseTimeStamp(timeStamp: any): Timestamp {
  if (!timeStamp) return null;

  if (timeStamp instanceof firebase.firestore.Timestamp) {
    return timeStamp as Timestamp;
  }

  if (timeStamp && typeof timeStamp === "object") {
    // Has seconds and nanoseconds properties ?
    if (timeStamp.seconds !== undefined && timeStamp.nanoseconds !== undefined) {
      return new firebase.firestore.Timestamp(timeStamp.seconds, timeStamp.nanoseconds) as Timestamp;
    }

    // Has _seconds and _nanoseconds properties ?
    if (timeStamp._seconds !== undefined && timeStamp._nanoseconds !== undefined) {
      return new firebase.firestore.Timestamp(timeStamp._seconds, timeStamp._nanoseconds) as Timestamp;
    }

    // n __time__ format ? (a serialized value perhaps)
    if (timeStamp.__time__) {
      return firebase.firestore.Timestamp.fromDate(new Date(timeStamp.__time__)) as Timestamp;
    }
  }

  // legacy DD/MM/YYYY format ?
  if (typeof timeStamp === "string") {
    if (timeStamp.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = timeStamp.split("/");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return firebase.firestore.Timestamp.fromDate(date) as Timestamp;
    }

    // default fallback
    return firebase.firestore.Timestamp.fromDate(new Date(timeStamp)) as Timestamp;
  }

  // Fallback
  return firebase.firestore.Timestamp.fromDate(new Date(timeStamp)) as Timestamp;
}

export const getWidgetRoute = (options: WidgetRouteOptions): string => {
  const baseParams = `clientId=${options.clientId}&restaurantId=${options.restaurantId}&type=${options.widgetType}`;
  switch (options.widgetType) {
    case "bookingNew":
      return `/booking/wizard?${baseParams}&widgetId=${options.widgetId}&theme=default`;

    case "functionNew":
      return `/functions/wizard?${baseParams}`;

    case "takeawayNew":
      return `/booking/takeaway?${baseParams}&widgetId=${options.widgetId}&theme=default`;

    case "takeawayReorder":
      return `/ordering?${baseParams}&isReorder=true&?widgetId=${options.widgetId}&date=${options.date}&mealId=${options.mealId}&bookingId=${options.bookingId}${options.customerId ? `&userId=${options.customerId}` : ""}`;

    case "bookingHub":
      return `/booking/hub?${baseParams}&date=${options.date}&mealId=${options.mealId}&bookingId=${options.bookingId}${options.customerId ? `&userId=${options.customerId}` : ""}`;

    case "bookingEdit":
      return `/edit-booking?${baseParams}&date=${options.date}&mealId=${options.mealId}&bookingId=${options.bookingId}${options.customerId ? `&userId=${options.customerId}` : ""}`;

    case "membershipApp":
      return `/${options.tenantId}/restaurants/${options.restaurantId}`;

    case "qrBooking":
      return `/booking/qr-booking?${baseParams}&areaId=${options.areaId}&clientId=${options.clientId}&widgetId=${options.widgetId}&tableNumber=${options.tableNumber}`;

    case "venueOrdering":
      return `/ordering?${baseParams}&date=${options.date}&mealId=${options.mealId}&bookingId=${options.bookingId}&userId=${options.userId}&bookingClass=${options.bookingClass}`;

    case "giftCertificate":
      return `/giftCertificates?${baseParams}&d=2`;

    default:
      return "";
  }
};

export const getWidgetUrl = (options: WidgetRouteOptions): string => {
  const widgetRoute = getWidgetRoute(options);
  return widgetRoute ? `${options.widgetPath}${widgetRoute}` : "#";
};

//  Find item in nested array that match given target Id
export const findItemInList = <T>(list: T[], childrenKey: keyof T, targetId: string): T => {
  const flattenDeep = (nodes: T[]): T[] => {
    return flatMap(nodes, (node) => {
      const children = node[childrenKey];
      return [node, ...(Array.isArray(children) ? flattenDeep(children as T[]) : [])];
    });
  };

  const flatList = flattenDeep(list);
  return find(flatList, { id: targetId }) as T;
};

// given an id of the item find its ancestor ie top most parent
export const findAncestorInTree = (items: TreeData[], id: string): TreeData | undefined => {
  const visited = new Set<string>();
  let currentId: string | undefined = id;

  while (currentId) {
    // Check for circular reference
    if (visited.has(currentId)) {
      return undefined;
    }
    visited.add(currentId);

    const item = items.find((i) => i.id === currentId);

    if (!item) {
      return undefined;
    }

    if (!item.parentId) {
      return item;
    }

    currentId = item.parentId;
  }
  return undefined;
};

export const generateHmacHashData = (obj: unknown, passPhrase: string) => {
  const payload = JSON.stringify(obj);
  const timestamp = Date.now().toString();
  const signatureBase = `${timestamp}.${payload}`;

  const signature = crypto.createHmac("sha256", passPhrase).update(signatureBase).digest("hex");

  return { signature, timestamp };
};

export const generateJWTToken = (rotationDays = 90): string => {
  const token: string = uuidv4();
  const createdAt = new Date();
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + rotationDays);

  const payload = {
    token,
    createdAt,
    expiresAt,
    rotationDays,
  };
  const header = {
    alg: "none",
    typ: "JWT",
  };
  const base64 = (obj: any) => Buffer.from(JSON.stringify(obj)).toString("base64url");

  return `${base64(header)}.${base64(payload)}.`;
};

export function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
}

export const safeDivide = (a: number, b: number, fallback = 0): number => {
  if (!_.isFinite(b) || b === 0) return fallback;
  const result = _.divide(a, b);
  return _.isFinite(result) ? result : fallback;
};

/**
 * coverts given object to simple top level array (ignores nested) and picks
 * value | id | _key as preferred value
 */
export function toArray<T extends object>(obj: Partial<T>): string[] {
  return chain(obj)
    .values()
    .map((val) => {
      // Primitives
      if (isString(val) || isNumber(val)) {
        return String(val);
      }

      // parse objects
      if (isPlainObject(val)) {
        const preferred = get(val, "value") ?? get(val, "id") ?? get(val, "_key");

        if (isString(preferred) || isNumber(preferred)) {
          return String(preferred);
        }
      }

      return undefined;
    })
    .compact() // remove undefined / null
    .value();
}

/**
 * Calculates max depth of a nested object for a given fieldName
 * and returns the maximum number of children at each depth.
 *
 * @param {Object} obj - Object to inspect
 * @param {string} fieldName - Field containing nested children
 * @returns {{
 *   maxDepth: number;
 *   depthCounts: { depth: number; maxChildNumberInThisDepth: number }[];
 * }}
 */
export function getObjectDepthStats(obj: any, fieldName: string) {
  if (!obj || typeof obj !== "object" || !fieldName) {
    return { maxDepth: 0, depthCounts: [] };
  }

  const visited = new WeakSet();
  const depthMap: Record<number, number> = {};

  function traverse(node: any, depth: number): number {
    if (!node || typeof node !== "object") return depth - 1;
    if (visited.has(node)) return depth - 1;

    visited.add(node);

    const children = node[fieldName];
    const numChildren = children && typeof children === "object" ? Object.keys(children).length : 0;

    // Update max number of children at this depth

    let maxDepth = depth;

    if (numChildren > 0) {
      depthMap[depth + 1] = Math.max(depthMap[depth + 1] || 0, numChildren);

      for (const key of Object.keys(children)) {
        const childDepth = traverse(children[key], depth + 1);
        maxDepth = Math.max(maxDepth, childDepth);
      }
    }

    return maxDepth;
  }

  const maxDepth = traverse(obj, 0);

  // Convert depthMap -> array format
  const depthCounts = Object.keys(depthMap)
    .map((d) => ({ depth: Number(d), maxChildNumberInThisDepth: depthMap[Number(d)] }))
    .sort((a, b) => a.depth - b.depth);

  return { maxDepth, depthCounts };
}

/**
 *
 * @param obj
 * @param overrideOptions
 * @param formatToCurrency
 * @returns  object with price fields converted from cents to dollar
 */
export function convertObjectPriceFieldsToDollar(obj, overrideOptions = [], formatToCurrency = false) {
  const newObj = cloneDeep(obj);

  function convertFieldsToDollar(obj, fieldsToConvert) {
    function recurse(current) {
      if (_.isArray(current)) {
        _.forEach(current, recurse);
      } else if (_.isPlainObject(current)) {
        _.forOwn(current, (value, key) => {
          if (fieldsToConvert.includes(key) && _.isNumber(value)) {
            current[key] = formatToCurrency ? MoneyUtils.format(value) : MoneyUtils.centsToDollars(value);
          } else {
            recurse(value);
          }
        });
      }
    }
    recurse(obj);
    return obj;
  }
  convertFieldsToDollar(newObj, overrideOptions.length > 0 ? overrideOptions : ["grandTotal", "total", "price", "value", "salesTax", "totalSalesTax", "purchaseAmount", "subTotal", "serviceFeeAmount", "resbutlerFeeAmount"]);
  return newObj;
}

export function generateShortUrl(url: string, host: string): { url: string; code: string } {
  const code = generateRandomString(8);
  if (!url || !host)
    return {
      url: url || "",
      code,
    };
  const hostWithoutProtocol = host.replace(/^https?:\/\//, "");
  return { url: `${hostWithoutProtocol}/link/${code}`, code };
}

// =============================================================================
// URL rewriter primitives — environment-agnostic.
//
// Constants + pure predicates + pure walker functions used by the
// Phase 2 Schedule-trigger URL rewriter. The BE wires these together
// with a mint callback (the LinkMap RTDB writer lives in BE-only
// messageHelpers); FE can reuse the predicates for content-validation
// or unit tests.
// =============================================================================

// `<a ... href="..."` and `<a ... href='...'`. Splits prefix / quote /
// url / suffix so replacement preserves Unlayer's attribute order.
export const HREF_RX = /(<a\b[^>]*?\shref\s*=\s*)(["'])([^"']+)\2/gi;

// Plain http(s) URLs in text bodies. Greedy up to whitespace + common
// punctuation. Deliberately permissive — operators paste odd URLs.
export const URL_RX = /https?:\/\/[^\s<>"'`«»【】]+/gi;

// `/link/<code>` regardless of scheme + host — covers prod, staging,
// and any white-label domain that proxies to the same resolver.
export const WIZ_SHORTLINK_RX = /^(?:https?:\/\/)?[^/]+\/link\/[A-Za-z0-9]+/i;

export function isAlreadyShortlinked(url: string): boolean {
  return WIZ_SHORTLINK_RX.test(url);
}

// Skip list — links the rewriter must NEVER touch.
//
//   • mailto: / tel: — preserves user's mail/dialer flow. Not BQ engagement.
//   • Anchors (`#section`) — internal page jump, no host.
//   • Any unresolved merge tag (`{{...}}`, whole URL or inside its query) —
//     minting would lock the raw tag into RTDB. Resolve message-constant tags
//     first (resolveMessageConstantMergeTags); an unshortened link beats a broken one.
//   • Already-shortlinked — idempotency on re-runs.
export function shouldSkipUrlRewrite(url: string): boolean {
  if (!url) return true;
  if (url.startsWith("mailto:") || url.startsWith("tel:")) return true;
  if (url.startsWith("#")) return true;
  if (url.includes("{{")) return true;
  if (isAlreadyShortlinked(url)) return true;
  return false;
}

// Pure walker — collects every `<a href>` match position + URL.
// Caller mints shortlinks + rebuilds the string back-to-front.
//
// Two-pass to support async minting: collect -> mint -> replace.
// Dedup by long URL within one walk (K codes per message, not
// per-match — same URL appearing 5× mints ONE code).
export async function rewriteHtmlLinks(html: string, mint: (longUrl: string) => Promise<string>): Promise<{ html: string; rewritten: number; skipped: number }> {
  if (!html) return { html: html ?? "", rewritten: 0, skipped: 0 };

  const matches: Array<{ start: number; end: number; prefix: string; quote: string; url: string }> = [];
  HREF_RX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = HREF_RX.exec(html)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, prefix: m[1], quote: m[2], url: m[3] });
  }

  const dedup = new Map<string, string>();
  let skipped = 0;
  for (const match of matches) {
    if (shouldSkipUrlRewrite(match.url)) {
      skipped++;
      continue;
    }
    if (!dedup.has(match.url)) {
      dedup.set(match.url, await mint(match.url));
    }
  }

  let out = html;
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const shortUrl = dedup.get(match.url);
    if (!shortUrl) continue;
    out = out.slice(0, match.start) + `${match.prefix}${match.quote}${shortUrl}${match.quote}` + out.slice(match.end);
  }

  return { html: out, rewritten: dedup.size, skipped };
}

// Pure walker — collects plain URLs in text bodies, skipping any URL
// inside a `{{...}}` merge-tag span so the merge tag survives intact.
export async function rewriteBodyLinks(body: string, mint: (longUrl: string) => Promise<string>): Promise<{ body: string; rewritten: number; skipped: number }> {
  if (!body) return { body: body ?? "", rewritten: 0, skipped: 0 };

  const tagSpans: Array<[number, number]> = [];
  const tagRx = /\{\{[^}]+\}\}/g;
  let t: RegExpExecArray | null;
  while ((t = tagRx.exec(body)) !== null) {
    tagSpans.push([t.index, t.index + t[0].length]);
  }
  const insideTag = (idx: number) => tagSpans.some(([s, e]) => idx >= s && idx < e);

  const matches: Array<{ start: number; end: number; url: string }> = [];
  URL_RX.lastIndex = 0;
  let u: RegExpExecArray | null;
  while ((u = URL_RX.exec(body)) !== null) {
    if (insideTag(u.index)) continue;
    matches.push({ start: u.index, end: u.index + u[0].length, url: u[0] });
  }

  const dedup = new Map<string, string>();
  let skipped = 0;
  for (const match of matches) {
    if (shouldSkipUrlRewrite(match.url)) {
      skipped++;
      continue;
    }
    if (!dedup.has(match.url)) {
      dedup.set(match.url, await mint(match.url));
    }
  }

  let out = body;
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const shortUrl = dedup.get(match.url);
    if (!shortUrl) continue;
    out = out.slice(0, match.start) + shortUrl + out.slice(match.end);
  }

  return { body: out, rewritten: dedup.size, skipped };
}

// Clone of Client SDK Timestamp to work around issues with api and data enforcing admin Timestamp on write
export declare class Timestamp {
  /**
   * The number of seconds of UTC time since Unix epoch 1970-01-01T00:00:00Z.
   */
  readonly seconds: number;
  /**
   * The fractions of a second at nanosecond resolution.*
   */
  readonly nanoseconds: number;
  /**
   * Creates a new timestamp with the current date, with millisecond precision.
   *
   * @returns a new timestamp representing the current date.
   */
  static now(): Timestamp;
  /**
   * Creates a new timestamp from the given date.
   *
   * @param date - The date to initialize the `Timestamp` from.
   * @returns A new `Timestamp` representing the same point in time as the given
   *     date.
   */
  static fromDate(date: Date): Timestamp;
  /**
   * Creates a new timestamp from the given number of milliseconds.
   *
   * @param milliseconds - Number of milliseconds since Unix epoch
   *     1970-01-01T00:00:00Z.
   * @returns A new `Timestamp` representing the same point in time as the given
   *     number of milliseconds.
   */
  static fromMillis(milliseconds: number): Timestamp;
  /**
   * Creates a new timestamp.
   *
   * @param seconds - The number of seconds of UTC time since Unix epoch
   *     1970-01-01T00:00:00Z. Must be from 0001-01-01T00:00:00Z to
   *     9999-12-31T23:59:59Z inclusive.
   * @param nanoseconds - The non-negative fractions of a second at nanosecond
   *     resolution. Negative second values with fractions must still have
   *     non-negative nanoseconds values that count forward in time. Must be
   *     from 0 to 999,999,999 inclusive.
   */
  constructor(
    /**
     * The number of seconds of UTC time since Unix epoch 1970-01-01T00:00:00Z.
     */
    seconds: number,
    /**
     * The fractions of a second at nanosecond resolution.*
     */
    nanoseconds: number
  );
  /**
   * Converts a `Timestamp` to a JavaScript `Date` object. This conversion
   * causes a loss of precision since `Date` objects only support millisecond
   * precision.
   *
   * @returns JavaScript `Date` object representing the same point in time as
   *     this `Timestamp`, with millisecond precision.
   */
  toDate(): Date;
  /**
   * Converts a `Timestamp` to a numeric timestamp (in milliseconds since
   * epoch). This operation causes a loss of precision.
   *
   * @returns The point in time corresponding to this timestamp, represented as
   *     the number of milliseconds since Unix epoch 1970-01-01T00:00:00Z.
   */
  toMillis(): number;
  /**
   * Returns true if this `Timestamp` is equal to the provided one.
   *
   * @param other - The `Timestamp` to compare against.
   * @returns true if this `Timestamp` is equal to the provided one.
   */
  isEqual(other: Timestamp): boolean;
  /** Returns a textual representation of this `Timestamp`. */
  toString(): string;
  /** Returns a JSON-serializable representation of this `Timestamp`. */
  toJSON(): {
    seconds: number;
    nanoseconds: number;
  };
  /**
   * Converts this object to a primitive string, which allows `Timestamp` objects
   * to be compared using the `>`, `<=`, `>=` and `>` operators.
   */
  valueOf(): string;
}

// Converts "snake_case" keys to "camelCase"
export function keysToCamelCase<T = Record<string, any>>(input: any): T {
  if (Array.isArray(input)) {
    return input.map(keysToCamelCase) as any;
  }

  if (_.isPlainObject(input)) {
    return Object.entries(input).reduce(
      (acc, [key, value]) => {
        const camelKey = _.camelCase(key);
        acc[camelKey] = keysToCamelCase(value);
        return acc;
      },
      {} as Record<string, any>
    ) as T;
  }

  return input;
}

export const percentage = (numerator: number | undefined, denominator: number | undefined, decimals = 1): number => {
  if (!denominator || denominator <= 0) return 0;
  return Number((((numerator ?? 0) / denominator) * 100).toFixed(decimals));
};

export function getMenuItem(product: Product, restaurantId: string, quantity: number, pricingTiers: PriceReleaseSchedule[], bookingDate: number, intervalId: number): MenuItem {
  let price = 0,
    isChild = false;
  if (product.fixedPriceMenuTypes.adult === "adult" && product.restaurants?.[restaurantId]?.price) {
    price = Number(product.restaurants[restaurantId].price["adult"]);
  } else if (product.fixedPriceMenuTypes.adult === "child" && product.restaurants?.[restaurantId]?.price) {
    price = Number(product.restaurants[restaurantId].price["child"]);
    isChild = true;
  }

  // Apply pricing tier override if applicable
  if (pricingTiers?.length && bookingDate) {
    const booking = moment(String(bookingDate), "YYYYMMDD").add(intervalId, "milliseconds");
    const now = moment().startOf("day");
    const daysBeforeBooking = booking.diff(now, "days");

    const matchingTier = pricingTiers.find((tier) => daysBeforeBooking >= tier.startDays && daysBeforeBooking <= tier.endDays);
    if (matchingTier) {
      price = matchingTier.price;
    }
  }

  return {
    unit: Number(price),
    amount: Number(price) * quantity,
    productName: product.name,
    text: [],
    quantity,
    productId: product.id,
    isChild,
  } as MenuItem;
}

export const getUrlParams = (hash): [string, string, string, URLSearchParams] => {
  let result = hash ? hash.slice(hash.indexOf("#") + 1).split("/") : [];
  if (result && result.length && result.length < 3 && result[result.length - 1].indexOf("?") > 1) {
    // no mealId
    const index = result[result.length - 1].indexOf("?");
    const query = new URLSearchParams(result[result.length - 1].substring(index + 1, result[result.length - 1].length));
    result = [...result.slice(0, result.length - 1), result[result.length - 1].substring(0, index), "", query];
  } else if (result && result.length && result[result.length - 1].indexOf("?") > 1) {
    const index = result[result.length - 1].indexOf("?");
    const query = new URLSearchParams(result[result.length - 1].substring(index + 1, result[result.length - 1].length));
    result = [...result.slice(0, result.length - 1), result[result.length - 1].substring(0, index), query];
  }
  return result.map((r) => (typeof r === "string" ? decodeURIComponent(r) : r));
};

// Grab restaurant id in url
export const extractUrlRestaurantId = (allRestaurants: Restaurants | undefined, hash: string, search: string): string | undefined => {
  if (!allRestaurants) return undefined;
  const fromQuery = new URLSearchParams(search).get("restaurantId");
  if (fromQuery && allRestaurants[fromQuery]) return fromQuery;
  const raw = hash ? hash.slice(hash.indexOf("#") + 1) : "";
  const [segments, hashQuery] = raw.split("?");
  const fromHashQuery = hashQuery ? new URLSearchParams(hashQuery).get("restaurantId") : null;
  if (fromHashQuery && allRestaurants[fromHashQuery]) return fromHashQuery;
  for (const segment of (segments || "").split("/")) {
    const id = decodeURIComponent(segment || "");
    if (id && allRestaurants[id]) return id;
  }
  return undefined;
};

export function getMealsFiltered(date: string, meals: Meals, labels: Labels, restaurants: Restaurants, restaurantId: string) {
  const restaurant = restaurants[restaurantId];
  let mealsFiltered: Meals = {};
  if (restaurant) {
    const now = moment.tz(restaurant.zoneId);
    const dateMaestro = moment.tz(date, "YYYYMMDD", restaurant.zoneId).endOf("day");
    if (!now.isSameOrAfter(dateMaestro) && labels) {
      const mealLabels = getMealLabelIds(labels, restaurant.zoneId, date, restaurantId);
      mealsFiltered = pickBy(meals, (meal, mId) => meal.restaurantId === restaurantId && meal.enabled && findKey(mealLabels, (ml) => ml.mealId === mId) !== undefined);
    } else {
      mealsFiltered = pickBy(meals, (meal) => meal.restaurantId === restaurantId);
    }
  }
  return mealsFiltered;
}

export function getLineItemsTotalNotSubjectToTax(lineItems: LineItem[], isStandardTotal: boolean, paymentPriceAdjustments: PaymentPriceAdjustmentPayload[], surcharges: TransactionSurcharge[], discounts: number) {
  if (!paymentPriceAdjustments?.length && !discounts) return sumBy(lineItems, (li) => (li.salesTax === 0 ? li.total : 0));

  //proportionally increase/decrease line items by the adjustment
  const field = isStandardTotal ? "standardTotal" : "total";

  const totalNotSubjectToTax = chain(lineItems)
    .filter((li) => li.salesTax === 0 && li[field] > 0)
    .sumBy((li) => li[field])
    .value();

  const adjustmentTotal =
    chain(paymentPriceAdjustments)
      .filter((p) => !p.lineItemId)
      .sumBy((p) => (p.accountGroupAdjustmentType === ProductGroupPriceAdjustmentType.Increase ? p.change : -p.change))
      .value() - discounts;

  const totalTransactionValue = sumBy(surcharges, (s) => s.value) + sumBy(lineItems, (li) => li[field]);

  const ratio = adjustmentTotal / totalTransactionValue;

  return MoneyUtils.round(totalNotSubjectToTax + MoneyUtils.multiply(totalNotSubjectToTax, ratio));
}

const BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;

function base64ToBytes(b64: string): Uint8Array {
  if (typeof Buffer !== "undefined") return new Uint8Array(Buffer.from(b64, "base64")); // server
  const bin = atob(b64); // client
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function binaryStringToBytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

function toBytes(value: unknown): Uint8Array | null {
  if (value == null) return null;
  if (value instanceof Uint8Array) return value; // admin Buffer / raw
  const v = value as {
    toUint8Array?: unknown;
    _byteString?: { binaryString?: unknown };
    _delegate?: { _byteString?: { binaryString?: unknown } };
  };
  if (typeof v.toUint8Array === "function") return (value as { toUint8Array(): Uint8Array }).toUint8Array();
  // serialized Firestore Bytes that lost its prototype
  const bs = v._byteString?.binaryString ?? v._delegate?._byteString?.binaryString;
  if (typeof bs === "string") return binaryStringToBytes(bs);
  return null;
}

export function decodeField<V>(value: unknown): V | undefined {
  if (value == null) return value as undefined;

  const bytes = toBytes(value);
  if (bytes) {
    if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
      return JSON.parse(strFromU8(gunzipSync(bytes))) as V;
    }
    return JSON.parse(strFromU8(bytes)) as V; // bytes but not gzipped
  }

  if (typeof value === "string") {
    // new canonical format: gzipped JSON stored as base64
    if (BASE64_RE.test(value)) {
      const b = base64ToBytes(value);
      if (b.length >= 2 && b[0] === 0x1f && b[1] === 0x8b) {
        return JSON.parse(strFromU8(gunzipSync(b))) as V;
      }
    }
    return JSON.parse(value) as V; // legacy JSON string
  }

  return value as V; // legacy plain object
}

export function sortTablesByAssignment(boxes: Table[], sectionId: string, assignment: Assignment) {
  function getValue(key, item, index) {
    let vals = [];
    if (assignment.sections[sectionId] && assignment.sections[sectionId].order) {
      if (assignment.sections[sectionId].order[key] !== undefined) {
        return assignment.sections[sectionId].order[key];
      }
      vals = keys(assignment.sections[sectionId].order).map(function (key) {
        return assignment.sections[sectionId].order[key];
      });
      return min(vals) - index;
    }
    return item;
  }
  let tablesCombined = Object.assign({}, assignment.sections[sectionId]?.reserves, assignment.sections[sectionId]?.order);
  tablesCombined = pickBy(tablesCombined, (_table, boxId) => !(findIndex(boxes, (b) => b._key === boxId) === -1 && assignment.sections[sectionId]?.reserves?.[boxId] === undefined));
  const tables = keys(tablesCombined).map((key, index) => ({ key, value: getValue(key, tablesCombined[key], index + 1) }));
  return chain(tables).sortBy(["value"]).map("key").value().reverse();
}

export function buildCurlCommand(url: string, body: any, headers: Record<string, string> = {}): string {
  const headerFlags = Object.entries(headers)
    .map(([key, value]) => `-H '${key}: ${value}'`)
    .join(" \\\n  ");

  const escapedBody = JSON.stringify(body, null, 2).replace(/'/g, "'\\''"); // escape single quotes for shell safety

  return [`curl -X POST '${url}' \\`, `  -H 'Content-Type: application/json' \\`, headerFlags ? `  ${headerFlags} \\` : null, `  -d '${escapedBody}'`].filter(Boolean).join("\n");
}

export const parseJSON = (v: any, fallback: any) => {
  if (v == null) return fallback;
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return fallback;
    }
  }
  return v;
};

export const toMs = (ts: any): number | undefined => (ts ? new Date(ts.value ?? ts).getTime() : undefined);
export const getIntervalRanges = (from: number, to: number, interval = 30) => {
  const timesArr: number[] = [];
  let current = from;
  while (current <= to) {
    const date = current;
    if (!timesArr.includes(date) && from != current) timesArr.push(date);
    current += interval * 60 * 1000;
  }
  return timesArr;
};
